/**
 * Import artwork (Adobe Illustrator / PDF / SVG) into the print-template editor.
 *
 * `.ai` files are PDF-compatible, so Illustrator + PDF take the same path.
 *
 * Two strategies:
 *  - SVG  → DECOMPOSE along the file's own LAYER TREE. Illustrator, Figma,
 *    Inkscape and Photoshop's SVG export all write their layers as nested <g>
 *    elements, so every <g> becomes a nested editor group and every drawable
 *    node becomes its own element — the same shape the .ai and .psd importers
 *    produce. Text/images/rects/ellipses/lines become native (fully editable)
 *    elements; everything the editor's element model can't represent (arbitrary
 *    paths, gradients, clips, masks) becomes its own vector element, serialized
 *    as a standalone SVG that carries its ancestor transforms, the document
 *    <defs> and any clip/mask context, so it draws exactly as the file did.
 *  - AI/PDF → render the page at high DPI into one faithful, locked "Artwork"
 *    image layer (pixel-perfect, never breaks). Not decomposed — for editable
 *    pieces, export the design as SVG and import that instead.
 *
 * The output shape feeds `usePrintEditorStore.importElements(items, canvasPatch)`.
 */
import DOMPurify from 'dompurify';
import * as pdfjsLib from 'pdfjs-dist';
import { CANVAS_MIN, CANVAS_MAX } from '../schema/documentSchema';
import { FONT_CATALOG, getFamily, ensureFontFamily, resolveVariant } from '../fonts';
import { resolveFontStack } from '../panels/propertyControls';
import { minTextBox } from './textMetrics';
import {
    isLegacyBanglaFont, isLegacyBanglaRun, legacySignatureCount,
    legacyBanglaToUnicode, legacyBanglaFamily,
} from './legacyBangla';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
).toString();

const SVG_NS = 'http://www.w3.org/2000/svg';

const clampCanvas = (v) => Math.max(CANVAS_MIN, Math.min(CANVAS_MAX, Math.round(v)));

/**
 * Page size for an imported artwork, kept PROPORTIONAL inside the editor's
 * canvas limits. Clamping each axis on its own silently distorts anything
 * smaller than CANVAS_MIN or larger than CANVAS_MAX (an 800×80 banner would
 * come in stretched), so scale both axes by one factor instead.
 */
function fitDocSize(w, h) {
    const width = Math.max(1, w);
    const height = Math.max(1, h);
    let s = Math.min(1, CANVAS_MAX / Math.max(width, height));
    if (Math.min(width, height) * s < CANVAS_MIN) {
        s = Math.min(CANVAS_MIN / Math.min(width, height), CANVAS_MAX / Math.max(width, height));
    }
    return {
        docW: clampCanvas(width * s),
        docH: clampCanvas(height * s),
        scale: s,
    };
}

export function isImportArtworkFile(file) {
    return /\.(ai|pdf|svg|psd|psb|tiff?)$/i.test(file?.name || '');
}

/* ------------------------------------------------------------------ *
 *  Colour helpers — computed styles come back as rgb()/rgba().
 * ------------------------------------------------------------------ */

function rgbToHex(rgb, opacity = 1) {
    if (!rgb || rgb === 'none' || rgb === 'transparent') return null;
    const m = rgb.match(/rgba?\(([^)]+)\)/i);
    if (!m) return rgb.startsWith('#') ? rgb : null;
    const parts = m[1].split(',').map((s) => parseFloat(s.trim()));
    const [r, g, b] = parts;
    const a = (parts.length > 3 ? parts[3] : 1) * (Number.isFinite(opacity) ? opacity : 1);
    const h = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
    const base = `#${h(r)}${h(g)}${h(b)}`;
    if (a >= 1) return base;
    return `${base}${h(a * 255)}`;
}

function fontWeightToNumber(w) {
    if (!w) return 400;
    if (w === 'normal') return 400;
    if (w === 'bold') return 700;
    const n = parseInt(w, 10);
    return Number.isFinite(n) ? n : 400;
}

function firstFontFamily(cssFamily) {
    if (!cssFamily) return 'Inter';
    return cssFamily.split(',')[0].trim().replace(/^['"]|['"]$/g, '') || 'Inter';
}

/* ------------------------------------------------------------------ *
 *  SVG → editable document
 * ------------------------------------------------------------------ */

/** Page size in user units — prefer the viewBox (the true coordinate system). */
function svgPageSize(svg) {
    const vb = svg.getAttribute('viewBox');
    if (vb) {
        const p = vb.split(/[\s,]+/).map(Number);
        if (p.length === 4 && p[2] > 0 && p[3] > 0) return { w: p[2], h: p[3], vb };
    }
    const w = parseFloat(svg.getAttribute('width')) || 0;
    const h = parseFloat(svg.getAttribute('height')) || 0;
    if (w > 0 && h > 0) return { w, h, vb: `0 0 ${w} ${h}` };
    return { w: 800, h: 600, vb: '0 0 800 600' };
}

/** An ancestor with clip/mask/filter/blend/partial-opacity means extracting a
 *  child in isolation would drop that context — leave such nodes in the base. */
function hasComplexAncestor(node, root) {
    let n = node.parentElement;
    while (n && n !== root) {
        const cs = getComputedStyle(n);
        if (
            (n.getAttribute && (n.getAttribute('clip-path') || n.getAttribute('mask') || n.getAttribute('filter')))
            || cs.clipPath !== 'none' || cs.mask !== 'none' || cs.filter !== 'none'
            || cs.mixBlendMode !== 'normal'
            || parseFloat(cs.opacity) < 1
        ) return true;
        n = n.parentElement;
    }
    return false;
}

/** True when the node is a plain translate/scale (no rotation/skew) — so an
 *  axis-aligned bounding box faithfully represents it. */
function isAxisAligned(node) {
    const m = typeof node.getCTM === 'function' ? node.getCTM() : null;
    if (!m) return true;
    return Math.abs(m.b) < 1e-3 && Math.abs(m.c) < 1e-3;
}

/* ------------------------------------------------------------------ *
 *  Multi-coloured type
 *
 *  Designers colour a word inside a headline ("Enhance your music
 *  **Experience**"), and every format records that the same way: one text object
 *  whose characters carry different fills — <tspan>s in SVG, style runs in a PSD
 *  type layer. The editor holds it the same way too (`colorSpans`, see
 *  utils/textRuns.js), so the import keeps ONE text element instead of splitting
 *  the headline into pieces that no longer wrap, align or edit as a unit.
 * ------------------------------------------------------------------ */

/**
 * A per-character colour list → colour spans, dropping anything already painted
 * by the element's own fill.
 */
function spansFromPerChar(colors, baseColor) {
    const spans = [];
    let i = 0;
    while (i < colors.length) {
        const c = colors[i];
        if (!c || c === baseColor) { i += 1; continue; }
        let j = i + 1;
        while (j < colors.length && colors[j] === c) j += 1;
        spans.push({ start: i, end: j, color: c });
        i = j;
    }
    return spans;
}

/**
 * The pieces of an SVG <text>, each with the fill that actually applies to it.
 * Returns null when every piece is the same colour — the ordinary case, which
 * then takes the plain single-colour path below.
 */
function svgTextPieces(node) {
    const pieces = [];
    const walk = (el, inherited) => {
        el.childNodes.forEach((child) => {
            if (child.nodeType === 3) {
                if (child.nodeValue) pieces.push({ text: child.nodeValue, color: inherited });
                return;
            }
            if (child.nodeType !== 1) return;
            const cs = getComputedStyle(child);
            const own = cs.fill && cs.fill !== 'none' && !cs.fill.startsWith('url')
                ? rgbToHex(cs.fill, parseFloat(cs.fillOpacity))
                : null;
            walk(child, own || inherited);
        });
    };
    const rootCs = getComputedStyle(node);
    walk(node, rgbToHex(rootCs.fill, parseFloat(rootCs.fillOpacity)));
    const colors = new Set(pieces.map((p) => p.color));
    return colors.size > 1 ? pieces : null;
}

/**
 * Collapse whitespace exactly as the single-colour path does
 * (`.replace(/\s+/g, ' ').trim()`), carrying each surviving character's colour
 * with it, so the spans land on the characters they were written for.
 */
function collapsePiecesToSpans(pieces, baseColor) {
    const chars = [];
    pieces.forEach((p) => {
        [...String(p.text)].forEach((ch) => chars.push({ ch, color: p.color }));
    });

    const out = [];
    let pendingSpace = false;
    chars.forEach((c) => {
        if (/\s/.test(c.ch)) { pendingSpace = out.length > 0; return; }
        if (pendingSpace) { out.push({ ch: ' ', color: c.color }); pendingSpace = false; }
        out.push(c);
    });

    return {
        content: out.map((c) => c.ch).join(''),
        spans: spansFromPerChar(out.map((c) => c.color), baseColor),
    };
}

/**
 * One drawable node → a NATIVE editor element, or null when the editor's element
 * model can't hold it faithfully (rotation, gradient fill, pattern…) and the
 * caller should keep it as a vector element instead.
 *
 * `box` is already in document units; `sx`/`sy` convert a CSS px measured on the
 * live copy into the same document units (font sizes, stroke widths).
 */
function nodeToElement(node, box, sx, sy) {
    const tag = node.tagName.toLowerCase();
    const cs = getComputedStyle(node);

    if (tag === 'image') {
        if (!isAxisAligned(node)) return null;
        const href = node.getAttribute('href') || node.getAttributeNS(XLINK_NS, 'href');
        if (!href) return null;
        return {
            type: 'image', name: 'Image', src: href, fit: 'fill',
            x: box.x, y: box.y, width: box.width, height: box.height,
            opacity: parseFloat(cs.opacity) || 1,
            styles: {},
        };
    }

    if (tag === 'text' || tag === 'tspan') {
        if (!isAxisAligned(node)) return null;
        const raw = (node.textContent || '').replace(/\s+/g, ' ').trim();
        if (!raw) return null;
        const fill = cs.fill;
        if (!fill || fill === 'none' || fill.startsWith('url')) return null; // gradient/pattern text → vector
        const anchor = cs.textAnchor;
        const align = anchor === 'middle' ? 'center' : anchor === 'end' ? 'right' : 'left';
        const fontSize = Math.max(4, Math.round(parseFloat(cs.fontSize) * sy));
        // Same Bijoy rescue as the PDF / PSD paths: a file that names SutonnyMJ is
        // carrying Bangla as Latin bytes.
        const svgFont = firstFontFamily(cs.fontFamily);
        const legacyBangla = isLegacyBanglaFont(svgFont);
        const baseColor = rgbToHex(fill, parseFloat(cs.fillOpacity)) || '#000000';
        // Differently-filled <tspan>s stay ONE element, with the odd ones out kept
        // as colour spans. Skipped for Bijoy text: transliteration rewrites the
        // string character by character, so the offsets would no longer line up.
        const pieces = legacyBangla ? null : svgTextPieces(node);
        const multi = pieces ? collapsePiecesToSpans(pieces, baseColor) : null;
        const content = legacyBangla ? legacyBanglaToUnicode(raw) : (multi?.content || raw);
        return {
            type: 'text', name: content.slice(0, 24),
            ...(multi?.spans.length ? { colorSpans: multi.spans } : null),
            x: box.x, y: box.y, width: Math.ceil(box.width) + 2, height: Math.ceil(box.height) + 2,
            opacity: parseFloat(cs.opacity) || 1,
            content,
            // The box above is the ink extent of the font the FILE names. That face
            // is often missing here, so the box is re-fitted around the substitute
            // once the real face has loaded — same treatment the .ai/.psd text gets.
            _fitAnchor: {
                x: box.x, w: box.width,
                cx: box.x + box.width / 2, cy: box.y + box.height / 2,
                centred: false,
            },
            styles: {
                fontFamily: legacyBangla ? legacyBanglaFamily(svgFont) : psdFontFamily(svgFont),
                fontSize,
                fontWeight: fontWeightToNumber(cs.fontWeight),
                fontStyle: cs.fontStyle === 'italic' ? 'italic' : 'normal',
                color: rgbToHex(fill, parseFloat(cs.fillOpacity)) || '#000000',
                textAlign: align,
                verticalAlign: 'top',
                lineHeight: 1.15,
                letterSpacing: 0,
                whiteSpace: 'pre',
                backgroundColor: 'transparent',
            },
        };
    }

    if (tag === 'rect' || tag === 'circle' || tag === 'ellipse') {
        if (!isAxisAligned(node)) return null;
        const fill = rgbToHex(cs.fill, parseFloat(cs.fillOpacity));
        const fillUrl = cs.fill && cs.fill.startsWith('url');
        if (fillUrl) return null; // gradient/pattern fill → keep in base (lossless)
        const strokeHex = cs.stroke && cs.stroke !== 'none' && !cs.stroke.startsWith('url')
            ? rgbToHex(cs.stroke, parseFloat(cs.strokeOpacity)) : null;
        const strokeW = strokeHex ? Math.round((parseFloat(cs.strokeWidth) || 0) * sx) : 0;
        const isRect = tag === 'rect';
        const rx = isRect ? Math.round((parseFloat(node.getAttribute('rx')) || 0) * sx) : 0;
        return {
            type: 'shape', shape: isRect ? 'rect' : 'ellipse', name: isRect ? 'Rectangle' : 'Ellipse',
            x: box.x, y: box.y, width: box.width, height: box.height,
            opacity: parseFloat(cs.opacity) || 1,
            styles: {
                fill: fill || 'transparent', fillType: 'solid',
                stroke: strokeHex || '#000000', strokeWidth: strokeW, strokeStyle: 'solid',
                borderRadius: rx, borderTopLeftRadius: rx, borderTopRightRadius: rx,
                borderBottomRightRadius: rx, borderBottomLeftRadius: rx,
            },
        };
    }

    if (tag === 'line') {
        const strokeHex = cs.stroke && cs.stroke !== 'none' && !cs.stroke.startsWith('url')
            ? rgbToHex(cs.stroke, parseFloat(cs.strokeOpacity)) : null;
        if (!strokeHex) return null;
        const strokeW = Math.max(1, Math.round((parseFloat(cs.strokeWidth) || 1) * sy));
        return {
            type: 'shape', shape: 'line', name: 'Line',
            x: box.x, y: box.y,
            width: Math.max(box.width, strokeW), height: Math.max(box.height, strokeW),
            opacity: parseFloat(cs.opacity) || 1,
            styles: { fill: strokeHex, fillType: 'solid' },
        };
    }

    return null;
}

/* --- Node classification ------------------------------------------------- */

// Painted leaves. `use` pulls in a referenced shape; `svg`/`foreignObject` are
// self-contained islands — all three are kept whole as vector elements.
const SVG_DRAWABLE_TAGS = ['path', 'polygon', 'polyline', 'rect', 'circle', 'ellipse', 'line', 'image', 'text', 'use', 'svg', 'foreignObject'];
const SVG_DRAWABLE_SELECTOR = SVG_DRAWABLE_TAGS.join(',');
// Container tags whose children ARE the file's layer tree.
const SVG_GROUP_TAGS = new Set(['g', 'a', 'switch']);
/**
 * Definition subtrees: their contents only render through a reference, so their
 * leaves must never be walked as artwork.
 *
 * Every camel-cased tag is listed in BOTH spellings on purpose. A CSS type
 * selector is case-SENSITIVE for non-HTML elements, so `clippath` alone never
 * matches a real `<clipPath>` — and every path inside one would then be imported
 * as a visible layer of its own.
 */
const SVG_DEF_SELECTOR = 'defs,style,symbol,mask,pattern,marker,filter,'
    + 'clipPath,clippath,linearGradient,lineargradient,radialGradient,radialgradient';

const XLINK_NS = 'http://www.w3.org/1999/xlink';

/** The layer name a design tool wrote for this node, if any. Illustrator emits
 *  `data-name` whenever it had to sanitize the name into the `id`. */
function svgNodeName(node) {
    return node.getAttribute('data-name')
        || node.getAttribute('inkscape:label')
        || node.getAttribute('id')
        || null;
}

/** Painted leaves under `node` (itself included), definitions excluded. */
function svgDrawableCount(node, cache) {
    if (cache.has(node)) return cache.get(node);
    let n = Array.from(node.querySelectorAll(SVG_DRAWABLE_SELECTOR))
        .filter((el) => !el.closest(SVG_DEF_SELECTOR)).length;
    if (SVG_DRAWABLE_TAGS.includes(node.tagName)) n += 1;
    cache.set(node, n);
    return n;
}

/**
 * Everything a detached subtree might reference: the document's definition
 * blocks, plus any `<use>` target that lives outside them. Collected ONCE and
 * pasted into each vector element that actually references an id, so gradients,
 * clip paths, symbols and CSS rules survive the split without bloating the
 * elements that need none of it.
 */
function collectSvgDefs(root) {
    const parts = [];
    const seen = new Set();
    const ser = new XMLSerializer();
    const add = (node) => {
        if (!node || seen.has(node)) return;
        seen.add(node);
        parts.push(ser.serializeToString(node));
    };
    // Outermost definition blocks only — nested ones ride along inside them.
    Array.from(root.querySelectorAll(SVG_DEF_SELECTOR))
        .filter((n) => !n.parentElement || !n.parentElement.closest(SVG_DEF_SELECTOR))
        .forEach(add);
    // A `<use>` may point at ordinary artwork rather than a <symbol>.
    const wrapped = [];
    root.querySelectorAll('use').forEach((u) => {
        const href = u.getAttribute('href') || u.getAttributeNS(XLINK_NS, 'href') || '';
        if (!href.startsWith('#')) return;
        let target = null;
        try { target = root.querySelector(`#${CSS.escape(href.slice(1))}`); } catch { target = null; }
        if (!target || seen.has(target) || target.closest(SVG_DEF_SELECTOR)) return;
        seen.add(target);
        wrapped.push(ser.serializeToString(target));
    });
    if (wrapped.length) parts.push(`<defs>${wrapped.join('')}</defs>`);
    return parts.join('');
}

/**
 * Sibling nodes → a standalone SVG data URL cropped to `userBox` (SVG user
 * units), wrapped in shallow copies of every ancestor up to the root.
 *
 * The ancestor chain is what makes an extracted piece land in the right place at
 * the right size: transforms, inherited fills and any clip-path/mask in force
 * all live on those ancestors, and a node lifted out without them draws at the
 * wrong scale, in the wrong colour, untrimmed.
 *
 * `hoist` names properties the CALLER is going to re-apply on the editor element
 * (group opacity, blend mode). Those must be neutralised here or they are
 * applied twice — and a blend mode baked into a standalone SVG blends against
 * nothing, so it has to move to the element to blend against the artwork below.
 */
function svgNodesToVectorUrl(nodes, root, userBox, defs, hoist = null) {
    const cloneOf = (n) => {
        const clone = n.cloneNode(true);
        if (hoist) {
            // !important, because the value may come from a <style> rule that is
            // copied into this SVG's defs and would otherwise win again.
            if (hoist.opacity != null) clone.style.setProperty('opacity', '1', 'important');
            if (hoist.mixBlendMode) clone.style.setProperty('mix-blend-mode', 'normal', 'important');
        }
        return clone;
    };
    // A single node is NOT wrapped in a <g>: a lone <tspan> lifted out of its
    // <text> would then sit inside a <g> the text element may not contain, and
    // the whole run would silently fail to render.
    let inner;
    if (nodes.length === 1) inner = cloneOf(nodes[0]);
    else {
        inner = document.createElementNS(SVG_NS, 'g');
        nodes.forEach((n) => inner.appendChild(cloneOf(n)));
    }
    for (let a = nodes[0].parentElement; a && a !== root; a = a.parentElement) {
        const wrap = a.cloneNode(false); // shallow — siblings must not ride along
        wrap.appendChild(inner);
        inner = wrap;
    }

    const xml = new XMLSerializer().serializeToString(inner);
    const needsDefs = defs && /url\(#|(?:xlink:)?href\s*=\s*["']#/.test(xml);
    const w = Math.max(1, Math.round(userBox.width));
    const h = Math.max(1, Math.round(userBox.height));
    const svg = `<svg xmlns="${SVG_NS}" xmlns:xlink="${XLINK_NS}" `
        + `viewBox="${r2(userBox.x)} ${r2(userBox.y)} ${r2(userBox.width)} ${r2(userBox.height)}" `
        + `width="${w}" height="${h}">${needsDefs ? defs : ''}${xml}</svg>`;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/** Tags the editor has a first-class element for — everything else is vector. */
const SVG_NATIVE_TAGS = new Set(['text', 'tspan', 'image', 'rect', 'circle', 'ellipse', 'line']);
/** Leaves whose visibility is decided by fill/stroke rather than by content. */
const SVG_PAINTED_TAGS = new Set(['path', 'polygon', 'polyline', 'rect', 'circle', 'ellipse', 'line', 'text', 'tspan']);

// Caps mirroring the .ai importer: a very deep or very large group stays a single
// vector piece so a detailed illustration can't explode into thousands of layers.
const SVG_MAX_DEPTH = 6;
const SVG_MAX_GROUP_NODES = 120;
const SVG_MAX_ELEMENTS = 300;

const isNoPaint = (v) => !v || v === 'none' || v === 'transparent' || /rgba\([^)]*,\s*0\)$/.test(v);

/** Nothing this node draws would be visible — a slice/bounds rect, an empty
 *  text run. Importing them adds invisible layers the user then has to hunt. */
function paintsNothing(cs) {
    const noFill = isNoPaint(cs.fill) || parseFloat(cs.fillOpacity) === 0;
    const noStroke = isNoPaint(cs.stroke)
        || parseFloat(cs.strokeWidth) === 0 || parseFloat(cs.strokeOpacity) === 0;
    return noFill && noStroke;
}

/**
 * SVG → a document that mirrors the file's layer tree.
 *
 * Async because imported text is re-fitted around the fonts we can actually
 * load, which means waiting on the faces first.
 */
export async function svgToDocument(svgText) {
    const clean = DOMPurify.sanitize(svgText, {
        USE_PROFILES: { svg: true, svgFilters: true },
        ADD_TAGS: ['use'],
    });
    const parsed = new DOMParser().parseFromString(
        clean.includes('<svg') ? clean : `<svg xmlns="${SVG_NS}">${clean}</svg>`,
        'image/svg+xml',
    );
    const svg = parsed.querySelector('svg');
    if (!svg) throw new Error('No <svg> root found in file.');

    const { w: pageW, h: pageH, vb } = svgPageSize(svg);
    // An artboard outside the editor's page limits is scaled as a whole, so the
    // elements below have to be scaled by the same factor or they'd land off-page.
    const { docW, docH, scale: ds } = fitDocSize(pageW, pageH);

    // Live copy in the DOM so getBoundingClientRect / getComputedStyle / getCTM work.
    const host = document.createElement('div');
    // `font` is pinned to the SVG default rather than left to inherit: text that
    // names no font-family would otherwise pick up the APP's typeface and import
    // in a face the file never used.
    host.style.cssText = `position:fixed;left:-99999px;top:0;width:${pageW}px;height:${pageH}px;`
        + 'opacity:0;pointer-events:none;overflow:hidden;font:normal normal 400 16px/normal serif';
    const live = svg.cloneNode(true);
    live.setAttribute('viewBox', vb);
    live.setAttribute('width', `${pageW}`);
    live.setAttribute('height', `${pageH}`);
    live.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    host.appendChild(live);
    document.body.appendChild(host);

    const out = [];
    const groups = [];
    let mergedNodes = 0;
    try {
        const svgRect = live.getBoundingClientRect();
        const ux = svgRect.width ? pageW / svgRect.width : 1;
        const uy = svgRect.height ? pageH / svgRect.height : 1;
        // CSS px on the live copy → SVG user units (what a viewBox is written in).
        const toUser = (r) => ({
            x: (r.left - svgRect.left) * ux,
            y: (r.top - svgRect.top) * uy,
            width: r.width * ux,
            height: r.height * uy,
        });
        // …and user units → document units.
        const toDoc = (u) => ({
            x: Math.round(u.x * ds),
            y: Math.round(u.y * ds),
            width: Math.max(1, Math.round(u.width * ds)),
            height: Math.max(1, Math.round(u.height * ds)),
        });
        const unionUser = (a, b) => {
            if (!a) return b;
            const x = Math.min(a.x, b.x);
            const y = Math.min(a.y, b.y);
            return {
                x,
                y,
                width: Math.max(a.x + a.width, b.x + b.width) - x,
                height: Math.max(a.y + a.height, b.y + b.height) - y,
            };
        };
        const boxOf = (node) => {
            let rect;
            try { rect = node.getBoundingClientRect(); } catch { return null; }
            const user = toUser(rect);
            // A hairline is legitimately zero-thickness on one axis — only reject
            // what has no extent at all.
            if (user.width < 0.05 && user.height < 0.05) return null;
            return user;
        };

        const defs = collectSvgDefs(live);
        const counts = new Map();
        let z = 0;
        let nVector = 0;
        let nGroup = 0;
        let nMerged = 0; // nodes folded into a shared layer by the caps below

        const emitVector = (nodes, parentId, name, hoist = null) => {
            let user = null;
            nodes.forEach((n) => { const b = boxOf(n); if (b) user = unionUser(user, b); });
            if (!user) return;
            const box = toDoc(user);
            nVector += 1;
            const el = {
                type: 'image',
                name: name || `Path ${nVector}`,
                src: svgNodesToVectorUrl(nodes, live, user, defs, hoist),
                fit: 'fill',
                styles: {},
                x: box.x, y: box.y, width: box.width, height: box.height,
                zIndex: z++,
                groupId: parentId,
            };
            if (hoist?.opacity != null) el.opacity = hoist.opacity;
            if (hoist?.mixBlendMode) el.styles.mixBlendMode = hoist.mixBlendMode;
            out.push(el);
        };

        const emitLeaf = (node, parentId) => {
            const user = boxOf(node);
            if (!user) return;
            const tag = node.tagName.toLowerCase();

            // A multi-line <text> positions each line with its own placed <tspan>.
            // Collapsing them into one element would stack every line on the first.
            if (tag === 'text') {
                const lines = Array.from(node.querySelectorAll('tspan'))
                    .filter((t) => t.hasAttribute('x') || t.hasAttribute('y'));
                if (lines.length > 1) {
                    lines.forEach((t) => emitLeaf(t, parentId));
                    return;
                }
            }

            const cs = getComputedStyle(node);
            const blend = cs.mixBlendMode !== 'normal' ? cs.mixBlendMode : null;
            // A clip, mask or filter the node carries ITSELF has no equivalent in the
            // editor's element model, and getBoundingClientRect reports the shape's
            // UNCLIPPED extent — so a natively extracted element would come in the
            // wrong size with the trimming silently gone. Keep those as vector.
            const selfComplex = node.hasAttribute('clip-path') || node.hasAttribute('mask')
                || node.hasAttribute('filter')
                || cs.clipPath !== 'none' || cs.filter !== 'none';

            if (SVG_NATIVE_TAGS.has(tag) && !selfComplex && !hasComplexAncestor(node, live)) {
                let spec = null;
                try { spec = nodeToElement(node, toDoc(user), ux * ds, uy * ds); } catch { spec = null; }
                if (spec) {
                    spec.zIndex = z++;
                    spec.groupId = parentId;
                    spec.name = svgNodeName(node) || spec.name;
                    if (blend) spec.styles.mixBlendMode = blend;
                    out.push(spec);
                    return;
                }
            }
            emitVector([node], parentId, svgNodeName(node), blend ? { mixBlendMode: blend } : null);
        };

        /** Children in DOCUMENT order — which is exactly SVG paint order, so the
         *  z-order we assign as we go is the stacking the file renders with. */
        const walk = (parent, parentId, depth) => {
            const kids = Array.from(parent.children).filter((el) => (
                !el.matches(SVG_DEF_SELECTOR)
                && (SVG_GROUP_TAGS.has(el.tagName) || SVG_DRAWABLE_TAGS.includes(el.tagName))
            ));
            const overflow = [];

            kids.forEach((node) => {
                const cs = getComputedStyle(node);
                if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) return;
                if (!svgDrawableCount(node, counts)) return;
                if (SVG_PAINTED_TAGS.has(node.tagName.toLowerCase()) && paintsNothing(cs)) return;

                // Past the element budget, whatever is left at this level is merged
                // into ONE piece rather than dropped — an over-detailed file loses
                // layers, never artwork.
                if (out.length >= SVG_MAX_ELEMENTS) { overflow.push(node); return; }

                if (!SVG_GROUP_TAGS.has(node.tagName)) { emitLeaf(node, parentId); return; }

                const opacity = parseFloat(cs.opacity);
                const blend = cs.mixBlendMode !== 'normal' ? cs.mixBlendMode : null;
                // Group opacity / blend / filter apply to the group's COMPOSITE, so
                // splitting the children changes how they overlap each other. Keep it
                // whole — and move opacity and blend onto the ELEMENT, because a blend
                // mode baked into a standalone SVG has nothing left to blend against.
                if (opacity < 1 || blend || cs.filter !== 'none') {
                    nGroup += 1;
                    emitVector([node], parentId, svgNodeName(node) || `Group ${nGroup}`, {
                        opacity: opacity < 1 ? opacity : null,
                        mixBlendMode: blend,
                    });
                    return;
                }
                if (depth >= SVG_MAX_DEPTH || svgDrawableCount(node, counts) > SVG_MAX_GROUP_NODES) {
                    nGroup += 1;
                    nMerged += svgDrawableCount(node, counts);
                    emitVector([node], parentId, svgNodeName(node) || `Group ${nGroup}`);
                    return;
                }

                const gid = randId('imp_grp');
                nGroup += 1;
                groups.push({ id: gid, name: svgNodeName(node) || `Group ${nGroup}`, parentId });
                walk(node, gid, depth + 1);
            });

            if (overflow.length) {
                nMerged += overflow.reduce((a, n) => a + svgDrawableCount(n, counts), 0);
                emitVector(overflow, parentId, 'Artwork');
            }
        };

        walk(live, null, 0);
        mergedNodes = nMerged;
    } finally {
        document.body.removeChild(host);
    }

    // The boxes above are the ink extent of the fonts the FILE names; re-fit them
    // around the faces we actually load, or long runs import clipped.
    await refitImportedText(out);

    return {
        canvas: { width: docW, height: docH, background: '#ffffff' },
        elements: out,
        groups,
        vector: true,
        layerCount: out.length,
        groupCount: groups.length,
        extractedText: out.filter((e) => e.type === 'text').length,
        editableCount: out.filter((e) => e.type !== 'image').length,
        // Artwork that came in whole but as a shared layer rather than one layer
        // per object, because the caps above kicked in. Reported, never silent.
        mergedNodes,
        pageCount: 1,
    };
}

/* ------------------------------------------------------------------ *
 *  AI / PDF → editable text + faithful vector base
 *
 *  `.ai` is PDF-compatible, so Illustrator text objects survive as real PDF
 *  text runs — pdf.js `getTextContent()` gives each run's string, position,
 *  size and font. We turn every run into an editable text element (colour
 *  sampled from the render), ERASE those runs from the rendered base so nothing
 *  is drawn twice, and keep the remaining vector art as the base image layer.
 * ------------------------------------------------------------------ */

function randId(prefix) {
    return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

const clamp255 = (v) => Math.max(0, Math.min(255, Math.round(v)));
const toHex = (r, g, b) => `#${clamp255(r).toString(16).padStart(2, '0')}${clamp255(g).toString(16).padStart(2, '0')}${clamp255(b).toString(16).padStart(2, '0')}`;

/** Strip a subset tag ("ABCDEF+Helvetica") and quotes; fall back sensibly. */
function cleanFontFamily(family, fontName) {
    let f = (family || '').split(',')[0].trim().replace(/^['"]|['"]$/g, '');
    f = f.replace(/^[A-Z]{6}\+/, '');
    if (!f || /^(sans-serif|serif|monospace)$/i.test(f)) {
        const n = fontName || '';
        if (/serif/i.test(n) && !/sans/i.test(n)) return 'Georgia';
        if (/mono|courier/i.test(n)) return 'Courier New';
        return 'Inter';
    }
    return f;
}

/** Coarse (16 levels/channel) colour bucket — the key both histograms below use. */
const bucketKey = (d, i) => ((d[i] >> 4) << 8) | ((d[i + 1] >> 4) << 4) | (d[i + 2] >> 4);

/** Mean of the exact pixels in one bucket, so the returned colour is precise
 *  rather than snapped to the bucket's 16-level grid. */
function meanOfBucket(data, key, predicate) {
    let r = 0; let g = 0; let b = 0; let n = 0;
    for (let i = 0; i < data.length; i += 4) {
        if (bucketKey(data, i) !== key) continue;
        if (predicate && !predicate(i)) continue;
        r += data[i]; g += data[i + 1]; b += data[i + 2]; n += 1;
    }
    return n ? { r: r / n, g: g / n, b: b / n, n } : null;
}

/** Approximate colour at the centre of a bucket, for cheap bucket-to-bucket
 *  distance without walking the pixels again. */
const bucketColor = (k) => ({
    r: ((k >> 8) & 15) * 16 + 8, g: ((k >> 4) & 15) * 16 + 8, b: (k & 15) * 16 + 8,
});
const colorDist = (a, b) => Math.abs(a.r - b.r) + Math.abs(a.g - b.g) + Math.abs(a.b - b.b);

/**
 * Sample a text run's ink colour and its local background from the rendered
 * canvas. Returns { color, bg, uniform }; `uniform` is false when the panel is
 * busy (photo/gradient) → caller skips erase.
 *
 * Both colours are picked by WHERE a colour sits in the box, not by how much of
 * the box it covers:
 *  - the background is whatever owns most of the box's border, because glyphs are
 *    inset and the panel behind them runs to the edges. Large type in a generous
 *    line box can out-cover its own backdrop, so "commonest wins" inverts.
 *  - the ink is the off-background colour most concentrated in the INTERIOR. A run
 *    that straddles two shades of the same panel (very common on print artwork,
 *    where bands cross behind a line of text) otherwise picks the neighbouring
 *    band as its ink and the text imports navy-on-navy.
 * Each is then averaged over its own exact pixels, so the result is a precise
 * colour rather than one snapped to the 16-level bucket grid — and it is the
 * MODAL bucket, not the mean of everything, because a glyph is mostly antialiased
 * edge at print sizes and averaging washes white out to grey.
 */
function sampleRun(ctx, x, y, w, h, cw, ch) {
    const rx = Math.max(0, Math.floor(x));
    const ry = Math.max(0, Math.floor(y));
    const rw = Math.min(cw - rx, Math.ceil(w));
    const rh = Math.min(ch - ry, Math.ceil(h));
    if (rw < 2 || rh < 2) return { color: null, bg: '#ffffff', uniform: true };

    let data;
    try { data = ctx.getImageData(rx, ry, rw, rh).data; } catch {
        return { color: null, bg: '#ffffff', uniform: true };
    }

    const total = rw * rh;
    const band = Math.max(1, Math.round(Math.min(rw, rh) * 0.12));

    // One pass: per-bucket total count and how much of it lies on the border.
    const hist = new Map();
    let borderN = 0;
    for (let p = 0; p < total; p += 1) {
        const i = p * 4;
        const px = p % rw;
        const py = (p / rw) | 0;
        const edge = px < band || px >= rw - band || py < band || py >= rh - band;
        if (edge) borderN += 1;
        const key = bucketKey(data, i);
        let e = hist.get(key);
        if (!e) { e = { n: 0, edge: 0 }; hist.set(key, e); }
        e.n += 1;
        if (edge) e.edge += 1;
    }

    let bgKey = 0; let bgEdge = -1; let bgCount = 0;
    hist.forEach((e, k) => {
        if (e.edge > bgEdge || (e.edge === bgEdge && e.n > bgCount)) {
            bgEdge = e.edge; bgCount = e.n; bgKey = k;
        }
    });
    const bgMean = meanOfBucket(data, bgKey) || { r: 255, g: 255, b: 255 };
    const bgBucket = bucketColor(bgKey);

    // Candidate inks: buckets clearly away from the background and big enough to
    // be glyph body rather than stray pixels.
    const minInk = Math.max(8, total * 0.01);
    const cands = [];
    hist.forEach((e, k) => {
        if (e.n < minInk) return;
        const dist = colorDist(bucketColor(k), bgBucket);
        if (dist <= 60) return;
        cands.push({ k, n: e.n, interior: 1 - e.edge / e.n, dist });
    });
    let inkKey = null; let inkCount = 0;
    if (cands.length) {
        // Interior concentration separates glyphs from a neighbouring panel; among
        // what is left, the FARTHEST colour from the background is the glyph body —
        // everything between the two is antialiasing, and taking a blend for the
        // ink is exactly what made white type import as grey.
        const best = Math.max(...cands.map((c) => c.interior));
        const pool = cands.filter((c) => c.interior >= best - 0.15);
        pool.sort((a, b) => (b.dist - a.dist) || (b.n - a.n));
        inkKey = pool[0].k;
        inkCount = pool[0].n;
    }

    let color = null;
    if (inkKey != null) {
        const isInk = (i) => (
            Math.abs(data[i] - bgMean.r) + Math.abs(data[i + 1] - bgMean.g)
            + Math.abs(data[i + 2] - bgMean.b)
        ) > 60;
        const m = meanOfBucket(data, inkKey, isInk) || meanOfBucket(data, inkKey);
        if (m) color = toHex(m.r, m.g, m.b);
    }

    // Flat panel when the background bucket plus the glyph bucket explain most of
    // the box — anything busier (photo, gradient) must not be painted over.
    const uniform = (bgCount + inkCount) > total * 0.9;
    return { color, bg: toHex(bgMean.r, bgMean.g, bgMean.b), uniform };
}

/* ------------------------------------------------------------------ *
 *  PDF vector decomposition — reconstruct the drawing objects (paths +
 *  groups) from the operator list, so an .ai/PDF imports LAYER-BY-LAYER as
 *  movable vector pieces (matching Illustrator's object tree) instead of one
 *  flat raster. Illustrator text is usually OUTLINED to paths, so this — not
 *  text extraction — is what recovers "layers" for most .ai designs.
 * ------------------------------------------------------------------ */

const applyM = (m, x, y) => [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];
// Concatenate matrices: PDF `cm M` sets CTM' = M then old CTM (apply b then a).
const mulM = (a, b) => [
    a[0] * b[0] + a[2] * b[1], a[1] * b[0] + a[3] * b[1],
    a[0] * b[2] + a[2] * b[3], a[1] * b[2] + a[3] * b[3],
    a[0] * b[4] + a[2] * b[5] + a[4], a[1] * b[4] + a[3] * b[5] + a[5],
];
const r2 = (n) => Math.round(n * 100) / 100;

/** pdf.js colour-operator args → hex. RGB arrives 0-255, gray/CMYK 0-1 — but
 *  both ranges have been seen in the wild, so each is range-guarded. */
function pdfColorHex(space, args) {
    if (space === 'rgb') {
        const s = (args[0] <= 1 && args[1] <= 1 && args[2] <= 1) ? 255 : 1;
        return toHex(args[0] * s, args[1] * s, args[2] * s);
    }
    if (space === 'gray') { const v = (args[0] <= 1 ? args[0] * 255 : args[0]); return toHex(v, v, v); }
    if (space === 'cmyk') {
        // Through the same press model the PSD path uses — and closer to what
        // pdf.js's own rasteriser draws, which is not a naive conversion either.
        const s = (args[0] <= 1 && args[1] <= 1 && args[2] <= 1 && args[3] <= 1) ? 100 : 100 / 255;
        const rgb = cmykToRgb255(args[0] * s, args[1] * s, args[2] * s, args[3] * s);
        return toHex(rgb.r, rgb.g, rgb.b);
    }
    return null;
}

/** Turn a pdf.js constructPath (sub-ops + flat numbers) into an SVG `d`,
 *  baking the current CTM so coordinates land in page (top-left) space. */
function buildPathD(subOps, nums, OPS, ctm) {
    let d = '';
    let k = 0;
    let cx = 0; let cy = 0;
    const dev = (x, y) => applyM(ctm, x, y);
    for (let s = 0; s < subOps.length; s += 1) {
        const op = subOps[s];
        if (op === OPS.moveTo) { const [X, Y] = dev(nums[k], nums[k + 1]); d += `M${r2(X)} ${r2(Y)}`; cx = X; cy = Y; k += 2; }
        else if (op === OPS.lineTo) { const [X, Y] = dev(nums[k], nums[k + 1]); d += `L${r2(X)} ${r2(Y)}`; cx = X; cy = Y; k += 2; }
        else if (op === OPS.curveTo) {
            const [X1, Y1] = dev(nums[k], nums[k + 1]);
            const [X2, Y2] = dev(nums[k + 2], nums[k + 3]);
            const [X3, Y3] = dev(nums[k + 4], nums[k + 5]);
            d += `C${r2(X1)} ${r2(Y1)} ${r2(X2)} ${r2(Y2)} ${r2(X3)} ${r2(Y3)}`; cx = X3; cy = Y3; k += 6;
        } else if (op === OPS.curveTo2) {
            const [X2, Y2] = dev(nums[k], nums[k + 1]);
            const [X3, Y3] = dev(nums[k + 2], nums[k + 3]);
            d += `C${r2(cx)} ${r2(cy)} ${r2(X2)} ${r2(Y2)} ${r2(X3)} ${r2(Y3)}`; cx = X3; cy = Y3; k += 4;
        } else if (op === OPS.curveTo3) {
            const [X1, Y1] = dev(nums[k], nums[k + 1]);
            const [X3, Y3] = dev(nums[k + 2], nums[k + 3]);
            d += `C${r2(X1)} ${r2(Y1)} ${r2(X3)} ${r2(Y3)} ${r2(X3)} ${r2(Y3)}`; cx = X3; cy = Y3; k += 4;
        } else if (op === OPS.rectangle) {
            const x = nums[k]; const y = nums[k + 1]; const w = nums[k + 2]; const h = nums[k + 3];
            const p0 = dev(x, y); const p1 = dev(x + w, y); const p2 = dev(x + w, y + h); const p3 = dev(x, y + h);
            d += `M${r2(p0[0])} ${r2(p0[1])}L${r2(p1[0])} ${r2(p1[1])}L${r2(p2[0])} ${r2(p2[1])}L${r2(p3[0])} ${r2(p3[1])}Z`;
            cx = p0[0]; cy = p0[1]; k += 4;
        } else if (op === OPS.closePath) { d += 'Z'; }
    }
    return d;
}

function bboxOfD(d) {
    const nums = (d.match(/-?\d+(?:\.\d+)?/g) || []).map(Number);
    let minx = Infinity; let miny = Infinity; let maxx = -Infinity; let maxy = -Infinity;
    for (let i = 0; i + 1 < nums.length; i += 2) {
        const x = nums[i]; const y = nums[i + 1];
        if (x < minx) minx = x; if (x > maxx) maxx = x;
        if (y < miny) miny = y; if (y > maxy) maxy = y;
    }
    return Number.isFinite(minx) ? { minx, miny, maxx, maxy } : null;
}

const growBox = (b, r) => (b && r ? {
    minx: b.minx - r, miny: b.miny - r, maxx: b.maxx + r, maxy: b.maxy + r,
} : b);

/** Overlap of two boxes, or null when they miss each other entirely. */
function intersectBox(a, b) {
    if (!a) return b || null;
    if (!b) return a;
    const r = {
        minx: Math.max(a.minx, b.minx), miny: Math.max(a.miny, b.miny),
        maxx: Math.min(a.maxx, b.maxx), maxy: Math.min(a.maxy, b.maxy),
    };
    return (r.maxx > r.minx && r.maxy > r.miny) ? r : null;
}

const unionBox = (a, b) => (a ? {
    minx: Math.min(a.minx, b.minx), miny: Math.min(a.miny, b.miny),
    maxx: Math.max(a.maxx, b.maxx), maxy: Math.max(a.maxy, b.maxy),
} : b);

/** Box of the unit square (PDF image/shading space) put through a matrix. */
function boxOfUnitSquare(m) {
    const pts = [applyM(m, 0, 0), applyM(m, 1, 0), applyM(m, 1, 1), applyM(m, 0, 1)];
    const xs = pts.map((p) => p[0]); const ys = pts.map((p) => p[1]);
    return {
        minx: Math.min(...xs), miny: Math.min(...ys),
        maxx: Math.max(...xs), maxy: Math.max(...ys),
    };
}

/**
 * A pdf.js image object → PNG data URL. pdf.js hands back either a decoded
 * ImageBitmap (browsers with OffscreenCanvas) or raw bytes tagged with a `kind`.
 */
function pdfImageToDataUrl(img, tint) {
    if (!img) return null;
    const w = img.width; const h = img.height;
    if (!w || !h || w * h > 40e6) return null;
    const c = newCanvas(w, h);
    const cx = c.getContext('2d');
    if (img.bitmap) {
        cx.drawImage(img.bitmap, 0, 0);
        return c.toDataURL('image/png');
    }
    const src = img.data;
    if (!src) return null;
    const out = cx.createImageData(w, h);
    const d = out.data;
    if (img.kind === 3) { // RGBA_32BPP
        d.set(src.subarray(0, w * h * 4));
    } else if (img.kind === 2) { // RGB_24BPP
        for (let i = 0, j = 0; i < w * h; i += 1) {
            d[j] = src[i * 3]; d[j + 1] = src[i * 3 + 1]; d[j + 2] = src[i * 3 + 2]; d[j + 3] = 255;
            j += 4;
        }
    } else if (img.kind === 1) { // GRAYSCALE_1BPP — row-padded bit packing
        // Stencil masks paint where the bit is 0; plain 1bpp images are B/W.
        const tintRgb = tint ? hexToRgb(tint) : null;
        const rowBytes = (w + 7) >> 3;
        for (let y = 0; y < h; y += 1) {
            for (let x = 0; x < w; x += 1) {
                const bit = (src[y * rowBytes + (x >> 3)] >> (7 - (x & 7))) & 1;
                const j = (y * w + x) * 4;
                if (tintRgb) {
                    d[j] = tintRgb.r; d[j + 1] = tintRgb.g; d[j + 2] = tintRgb.b;
                    d[j + 3] = bit ? 0 : 255;
                } else {
                    const v = bit ? 255 : 0;
                    d[j] = v; d[j + 1] = v; d[j + 2] = v; d[j + 3] = 255;
                }
            }
        }
    } else return null;
    cx.putImageData(out, 0, 0);
    return c.toDataURL('image/png');
}

function hexToRgb(hex) {
    const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ''));
    if (!m) return { r: 0, g: 0, b: 0 };
    const n = parseInt(m[1], 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/**
 * Walk the operator list into a tree of drawing objects for the visible layer.
 *
 * Fills alone are not enough to rebuild an Illustrator page: real artwork also
 * carries CLIPS (which decide how much of each shape is actually visible),
 * STROKES (hairlines, rules, trim marks), gradient SHADINGS and placed IMAGES.
 * Anything not reproduced here silently disappears from the import — and a
 * dropped clip is worse than a dropped shape, because the un-clipped shape then
 * paints over everything it was supposed to be trimmed against.
 */
async function pdfPageToVectorLayers(page, viewport, ocgNames) {
    const OPS = pdfjsLib.OPS;
    const { fnArray, argsArray } = await page.getOperatorList();
    // Placed images are decoded in the worker AFTER the operator list is handed
    // over — `getOperatorList()` only promises the ops, never their payloads. The
    // list says so itself: every image it references is announced by a
    // `dependency` op, and until those resolve `page.objs` has nothing under the
    // id, so every photo in the file would silently import as a missing layer.
    await awaitOperatorDependencies(page, fnArray, argsArray, OPS);

    const pageBox = { minx: 0, miny: 0, maxx: viewport.width, maxy: viewport.height };
    const CAPS = ['butt', 'round', 'square'];
    const JOINS = ['miter', 'round', 'bevel'];
    // Path coordinates are baked into device space, so stroke widths must be too.
    const ctmScale = (m) => Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2])) || 1;

    let ctm = viewport.transform.slice(); // pdf user space → top-left page px
    let gs = {
        fill: '#000000',
        fillAlpha: 1,
        stroke: '#000000',
        strokeAlpha: 1,
        lineWidth: 1,
        lineCap: 'butt',
        lineJoin: 'miter',
        dash: null,
        clips: [], // shared, copy-on-write — save/restore just keeps the array
    };
    let pendingD = '';
    let pendingClip = null;
    const gsStack = [];
    const root = [];
    const contStack = [root];
    let layerName = null;
    const top = () => contStack[contStack.length - 1];

    const clipBox = () => gs.clips.reduce((acc, c) => intersectBox(acc, c.box), null);

    const strokeProps = () => ({
        stroke: gs.stroke,
        strokeWidth: Math.max(0.1, gs.lineWidth * ctmScale(ctm)),
        strokeAlpha: gs.strokeAlpha,
        lineCap: gs.lineCap,
        lineJoin: gs.lineJoin,
        dash: gs.dash && gs.dash.length
            ? gs.dash.map((n) => r2(n * ctmScale(ctm))).join(' ')
            : null,
    });

    const emitPath = (filled, stroked, evenOdd) => {
        if (!pendingD) return;
        top().push({
            kind: 'path',
            d: pendingD,
            fill: filled ? gs.fill : null,
            alpha: gs.fillAlpha,
            rule: evenOdd ? 'evenodd' : 'nonzero',
            clips: gs.clips,
            ...(stroked ? strokeProps() : {}),
        });
    };

    for (let i = 0; i < fnArray.length; i += 1) {
        const fn = fnArray[i];
        const a = argsArray[i];
        if (fn === OPS.save) gsStack.push({ ctm: ctm.slice(), gs });
        else if (fn === OPS.restore) {
            const s = gsStack.pop();
            if (s) { ctm = s.ctm; gs = s.gs; }
        } else if (fn === OPS.transform) ctm = mulM(ctm, a);
        else if (fn === OPS.setFillRGBColor || fn === OPS.setFillGray || fn === OPS.setFillCMYKColor) {
            const space = fn === OPS.setFillRGBColor ? 'rgb' : (fn === OPS.setFillGray ? 'gray' : 'cmyk');
            const c = pdfColorHex(space, a);
            if (c) gs = { ...gs, fill: c };
        } else if (fn === OPS.setStrokeRGBColor || fn === OPS.setStrokeGray || fn === OPS.setStrokeCMYKColor) {
            const space = fn === OPS.setStrokeRGBColor ? 'rgb' : (fn === OPS.setStrokeGray ? 'gray' : 'cmyk');
            const c = pdfColorHex(space, a);
            if (c) gs = { ...gs, stroke: c };
        } else if (fn === OPS.setLineWidth) gs = { ...gs, lineWidth: a[0] };
        else if (fn === OPS.setLineCap) gs = { ...gs, lineCap: CAPS[a[0]] || 'butt' };
        else if (fn === OPS.setLineJoin) gs = { ...gs, lineJoin: JOINS[a[0]] || 'miter' };
        else if (fn === OPS.setDash) gs = { ...gs, dash: Array.isArray(a[0]) ? a[0] : null };
        else if (fn === OPS.setGState) {
            const next = { ...gs };
            (a[0] || []).forEach((pair) => {
                if (!Array.isArray(pair)) return;
                const [k, v] = pair;
                if (k === 'ca') next.fillAlpha = v;
                else if (k === 'CA') next.strokeAlpha = v;
                else if (k === 'LW') next.lineWidth = v;
                else if (k === 'LC') next.lineCap = CAPS[v] || 'butt';
                else if (k === 'LJ') next.lineJoin = JOINS[v] || 'miter';
                else if (k === 'D' && Array.isArray(v) && Array.isArray(v[0])) [next.dash] = v;
            });
            gs = next;
        } else if (fn === OPS.constructPath) {
            pendingD = buildPathD(a[0], a[1], OPS, ctm);
        } else if (fn === OPS.clip || fn === OPS.eoClip) {
            // `W`/`W*` arm the clip; it takes effect once the path op that follows
            // has run (almost always `n`, handled at endPath below).
            if (pendingD) pendingClip = { d: pendingD, rule: fn === OPS.eoClip ? 'evenodd' : 'nonzero' };
        } else if (fn === OPS.fill || fn === OPS.eoFill) {
            emitPath(true, false, fn === OPS.eoFill);
            pendingD = ''; applyPendingClip();
        } else if (fn === OPS.fillStroke || fn === OPS.eoFillStroke
            || fn === OPS.closeFillStroke || fn === OPS.closeEOFillStroke) {
            emitPath(true, true, fn === OPS.eoFillStroke || fn === OPS.closeEOFillStroke);
            pendingD = ''; applyPendingClip();
        } else if (fn === OPS.stroke || fn === OPS.closeStroke) {
            emitPath(false, true, false);
            pendingD = ''; applyPendingClip();
        } else if (fn === OPS.endPath) { pendingD = ''; applyPendingClip(); }
        else if (fn === OPS.shadingFill) {
            const box = intersectBox(clipBox(), pageBox);
            const ir = readShadingIR(page, a[0]);
            if (ir && box) top().push({ kind: 'shading', ir, ctm: ctm.slice(), clips: gs.clips, box });
        } else if (fn === OPS.paintImageXObject || fn === OPS.paintInlineImageXObject
            || fn === OPS.paintImageMaskXObject) {
            const raw = fn === OPS.paintImageXObject ? readPageObj(page, a[0]) : a[0];
            const isMask = fn === OPS.paintImageMaskXObject;
            const src = pdfImageToDataUrl(raw, isMask ? gs.fill : null);
            if (src) {
                // PDF image space is the unit square with row 0 at y = 1 — flip it.
                const m = mulM(ctm, [1, 0, 0, -1, 0, 1]);
                top().push({
                    kind: 'image', src, m, alpha: gs.fillAlpha, clips: gs.clips,
                    box: intersectBox(boxOfUnitSquare(m), clipBox() || pageBox),
                });
            }
        } else if (fn === OPS.beginGroup || fn === OPS.paintFormXObjectBegin) {
            const grp = { kind: 'group', objects: [] };
            top().push(grp);
            contStack.push(grp.objects);
        } else if (fn === OPS.endGroup || fn === OPS.paintFormXObjectEnd) {
            if (contStack.length > 1) contStack.pop();
        } else if (fn === OPS.beginMarkedContentProps) {
            const props = a[1];
            if (props && props.type === 'OCG' && ocgNames[props.id]) layerName = ocgNames[props.id];
        }
    }

    function applyPendingClip() {
        if (!pendingClip) return;
        const box = bboxOfD(pendingClip.d);
        gs = { ...gs, clips: [...gs.clips, { ...pendingClip, box }] };
        pendingClip = null;
    }

    return { objects: root, layerName };
}

/** An image pdf.js decided to cache across pages is sent to the DOCUMENT-wide
 *  store instead of the page's, under an id carrying the document prefix. Looking
 *  only in `page.objs` finds nothing for those — the store has to follow the id. */
const objStoreFor = (page, id) => (String(id).startsWith('g_') ? page.commonObjs : page.objs);

/** pdf.js parks shading/image payloads in `page.objs` under the id the operator
 *  carries. They are only there once resolved, so never let a miss throw. */
function readPageObj(page, id) {
    try {
        const store = objStoreFor(page, id);
        if (store?.has && !store.has(id)) return null;
        return store.get(id);
    } catch { return null; }
}

/**
 * Wait for the payloads the operator list declares it depends on (placed images,
 * fonts). pdf.js only awaits these inside its own canvas renderer, so a consumer
 * reading the op list directly has to do it too.
 *
 * Bounded: a payload that failed to decode resolves to null, but a store entry
 * that never resolves at all would otherwise hang the whole import — losing an
 * image is survivable, losing the import is not.
 */
async function awaitOperatorDependencies(page, fnArray, argsArray, OPS, ms = 20000) {
    const ids = new Set();
    for (let i = 0; i < fnArray.length; i += 1) {
        if (fnArray[i] === OPS.dependency) (argsArray[i] || []).forEach((id) => ids.add(id));
    }
    if (!ids.size) return;
    let timer = null;
    try {
        await Promise.race([
            Promise.all([...ids].map((id) => new Promise((resolve) => {
                try { objStoreFor(page, id).get(id, resolve); } catch { resolve(null); }
            }))),
            new Promise((resolve) => { timer = setTimeout(resolve, ms); }),
        ]);
    } finally {
        clearTimeout(timer);
    }
}

/** ["RadialAxial", type, bbox, colorStops, p0, p1, r0, r1] — anything else
 *  (mesh / dummy) has no faithful SVG equivalent, so it is skipped. */
function readShadingIR(page, id) {
    const ir = readPageObj(page, id);
    if (!Array.isArray(ir) || ir[0] !== 'RadialAxial') return null;
    const stops = ir[3];
    if (!Array.isArray(stops) || !stops.length) return null;
    return { type: ir[1], stops, p0: ir[4], p1: ir[5], r0: ir[6], r1: ir[7] };
}

function collectPaths(obj, acc) {
    if (obj.kind === 'group') obj.objects.forEach((o) => collectPaths(o, acc));
    else if (obj.kind) acc.push(obj);
}
/** Only real PATHS decide whether a page is worth decomposing. A scan is one big
 *  image with no vector art — it belongs in the raster fallback, where it stays a
 *  single faithful layer instead of being re-encoded as an element. */
function countAllPaths(objs) {
    const acc = [];
    objs.forEach((o) => collectPaths(o, acc));
    return acc.filter((o) => o.kind === 'path').length;
}

/** Visible extent of one drawing object: its own geometry, grown by half a
 *  stroke, then trimmed by every clip in force when it was painted. */
function bboxOfObj(obj) {
    let box = null;
    if (obj.kind === 'path') {
        box = bboxOfD(obj.d);
        if (obj.strokeWidth) box = growBox(box, obj.strokeWidth / 2);
    } else box = obj.box || null;
    if (!box) return null;
    (obj.clips || []).forEach((c) => { box = intersectBox(box, c.box); });
    return box;
}

/** Serialize drawing objects to a standalone SVG data URL, clips included. */
function pathsToSvgUrl(objs, bbox) {
    const w = bbox.maxx - bbox.minx;
    const h = bbox.maxy - bbox.miny;
    const defs = [];
    let uid = 0;

    const body = objs.map((o) => {
        let node = '';
        if (o.kind === 'path') {
            const strokeAttrs = o.stroke
                ? ` stroke="${o.stroke}" stroke-width="${r2(o.strokeWidth)}"`
                + `${o.strokeAlpha < 1 ? ` stroke-opacity="${r2(o.strokeAlpha)}"` : ''}`
                + `${o.lineCap && o.lineCap !== 'butt' ? ` stroke-linecap="${o.lineCap}"` : ''}`
                + `${o.lineJoin && o.lineJoin !== 'miter' ? ` stroke-linejoin="${o.lineJoin}"` : ''}`
                + `${o.dash ? ` stroke-dasharray="${o.dash}"` : ''}`
                : '';
            node = `<path d="${o.d}" fill="${o.fill || 'none'}"`
                + `${o.fill && o.alpha < 1 ? ` fill-opacity="${r2(o.alpha)}"` : ''}`
                + `${o.rule === 'evenodd' ? ' fill-rule="evenodd"' : ''}${strokeAttrs}/>`;
        } else if (o.kind === 'shading') {
            const gid = `g${uid += 1}`;
            const { ir } = o;
            const stops = ir.stops.map(([off, col]) => `<stop offset="${r2(Math.max(0, Math.min(1, off)))}" stop-color="${col}"/>`).join('');
            const gt = ` gradientUnits="userSpaceOnUse" gradientTransform="matrix(${o.ctm.map(r2).join(' ')})"`;
            defs.push(ir.type === 'radial'
                ? `<radialGradient id="${gid}" cx="${r2(ir.p1[0])}" cy="${r2(ir.p1[1])}" r="${r2(ir.r1)}" `
                    + `fx="${r2(ir.p0[0])}" fy="${r2(ir.p0[1])}" fr="${r2(ir.r0)}"${gt}>${stops}</radialGradient>`
                : `<linearGradient id="${gid}" x1="${r2(ir.p0[0])}" y1="${r2(ir.p0[1])}" `
                    + `x2="${r2(ir.p1[0])}" y2="${r2(ir.p1[1])}"${gt}>${stops}</linearGradient>`);
            const b = o.box;
            node = `<rect x="${r2(b.minx)}" y="${r2(b.miny)}" width="${r2(b.maxx - b.minx)}" `
                + `height="${r2(b.maxy - b.miny)}" fill="url(#${gid})"/>`;
        } else if (o.kind === 'image') {
            node = `<image href="${o.src}" x="0" y="0" width="1" height="1" `
                + `preserveAspectRatio="none" transform="matrix(${o.m.map((n) => r2(n)).join(' ')})"`
                + `${o.alpha < 1 ? ` opacity="${r2(o.alpha)}"` : ''}/>`;
        }
        // Innermost clip first, so the wrappers nest outward in paint order.
        (o.clips || []).slice().reverse().forEach((c) => {
            const cid = `c${uid += 1}`;
            defs.push(`<clipPath id="${cid}" clipPathUnits="userSpaceOnUse">`
                + `<path d="${c.d}"${c.rule === 'evenodd' ? ' clip-rule="evenodd"' : ''}/></clipPath>`);
            node = `<g clip-path="url(#${cid})">${node}</g>`;
        });
        return node;
    }).join('');

    const svg = `<svg xmlns="${SVG_NS}" viewBox="${r2(bbox.minx)} ${r2(bbox.miny)} ${r2(w)} ${r2(h)}" `
        + `width="${Math.max(1, Math.round(w))}" height="${Math.max(1, Math.round(h))}">`
        + `${defs.length ? `<defs>${defs.join('')}</defs>` : ''}${body}</svg>`;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/**
 * The font's REAL name (`SutonnyMJ`), not pdf.js's internal id (`g_d0_f1`).
 * Available once the page has been rendered or its operator list built, which
 * every import path here does before reading text.
 */
function pdfFontRealName(page, fontId) {
    try {
        const store = String(fontId).startsWith('g_') ? page?.commonObjs : page?.objs;
        if (!store?.has?.(fontId)) return '';
        const f = store.get(fontId);
        return String(f?.name || f?.fallbackName || '').replace(/^[A-Z]{6}\+/, '');
    } catch {
        return '';
    }
}

/**
 * Which of this page's fonts carry Bijoy-encoded Bangla — decided ONCE per font,
 * never per run. The same embedded face always uses the same encoding, and half
 * the runs of a Bijoy headline are pure ASCII with no tell of their own
 * (`ivLyb!` = রাখুন!), so a per-run test would convert one line and leave the next.
 *
 * The font's real name settles it when pdf.js exposes one. When it doesn't (an
 * embedded subset is just `g_d0_f1`), fall back to the encoding's signature — but
 * demand a run that converts to almost pure Bangla AND at least two signature
 * characters across the font, so an accented Latin word is never "converted".
 */
function legacyBanglaFonts(tc, page) {
    const runsByFont = new Map();
    tc.items.forEach((it) => {
        if (!it.str || !it.str.trim()) return;
        if (!runsByFont.has(it.fontName)) runsByFont.set(it.fontName, []);
        runsByFont.get(it.fontName).push(it.str);
    });

    const legacy = new Map();
    runsByFont.forEach((runs, id) => {
        const real = pdfFontRealName(page, id) || '';
        if (isLegacyBanglaFont(real)) { legacy.set(id, legacyBanglaFamily(real)); return; }
        // A name we can actually read and that isn't a legacy face settles it too.
        const named = real && !/^g_d\d|^[a-z]+_?\d+$/i.test(real);
        if (named) return;
        const signals = runs.reduce((n, r) => n + legacySignatureCount(r), 0);
        if (signals >= 2 && runs.some(isLegacyBanglaRun)) legacy.set(id, legacyBanglaFamily(real));
    });
    return legacy;
}

/** Degrees in (-180, 180], rounded to a tenth — what the editor stores. */
const normalizeAngle = (deg) => {
    let a = deg % 360;
    if (a > 180) a -= 360;
    if (a <= -180) a += 360;
    return Math.round(a * 10) / 10;
};

/**
 * Extract live PDF text runs → editable text elements (colour sampled from an
 * optional render context). Returns { textEls, erases }.
 *
 * Illustrator sets rotated type with a rotated text matrix. Skipping those runs
 * (the old behaviour) made every vertical label — the spine text on a business
 * card, a rotated price flash — vanish from the import, because the vector pass
 * has no glyphs of its own to fall back on. Instead, recover the angle from the
 * matrix and place the element in its own UNROTATED frame, centred on the spot
 * the rotated text actually occupies; the editor rotates about the centre, so
 * the glyphs land exactly where Illustrator drew them.
 */
function buildTextElements(tc, pageHpt, ptToDoc, ptToDev, ctx, cw, ch, page = null) {
    const textEls = [];
    const erases = [];
    const styles = tc.styles || {};
    // Bijoy-encoded fonts (SutonnyMJ and friends) hold Bangla as 8-bit Latin bytes;
    // their runs are transliterated below and re-typed in a Unicode Bangla face.
    const legacyFonts = legacyBanglaFonts(tc, page);
    tc.items.forEach((item) => {
        const raw = item.str || '';
        if (!raw.trim()) return;
        const legacyFamily = legacyFonts.get(item.fontName) || null;
        const str = legacyFamily ? legacyBanglaToUnicode(raw) : raw;
        const t = item.transform;
        const fontSize = Math.hypot(t[2], t[3]) || Math.abs(t[3]) || 12;
        if (fontSize < 2) return;
        // PDF space has y UP, the page has y DOWN — so the on-screen angle of the
        // baseline direction (t[0], t[1]) is atan2(-b, a).
        const rotation = normalizeAngle((Math.atan2(-t[1], t[0]) * 180) / Math.PI);
        const rad = (rotation * Math.PI) / 180;
        const dirX = Math.cos(rad); const dirY = Math.sin(rad);
        // Towards the ascender: the baseline direction turned a quarter left.
        const upX = Math.sin(rad); const upY = -Math.cos(rad);

        const wPt = item.width > 0 ? item.width : str.length * fontSize * 0.5;
        const st = styles[item.fontName] || {};
        const ascent = Number.isFinite(st.ascent) && st.ascent > 0 ? st.ascent : 0.8;
        const hPt = fontSize * 1.25;
        // Baseline start in page space, then walk to the run's visual centre.
        const originX = t[4];
        const originY = pageHpt - t[5];
        const lift = ascent * fontSize - hPt / 2;
        const cxPt = originX + dirX * (wPt / 2) + upX * lift;
        const cyPt = originY + dirY * (wPt / 2) + upY * lift;
        const xPt = cxPt - wPt / 2;
        const topPt = cyPt - hPt / 2;

        let color = '#111111';
        if (ctx) {
            // Sampling and erasing follow the run's true (rotated) footprint.
            const absC = Math.abs(dirX); const absS = Math.abs(dirY);
            const aabbW = wPt * absC + hPt * absS;
            const aabbH = wPt * absS + hPt * absC;
            const s = sampleRun(
                ctx, (cxPt - aabbW / 2) * ptToDev, (cyPt - aabbH / 2) * ptToDev,
                aabbW * ptToDev, aabbH * ptToDev, cw, ch,
            );
            if (s.color) color = s.color;
            if (s.uniform) {
                erases.push({
                    cx: cxPt * ptToDev, cy: cyPt * ptToDev,
                    w: wPt * ptToDev, h: hPt * ptToDev,
                    rotation, bg: s.bg || '#ffffff',
                });
            }
        }

        const elStyles = {
            fontFamily: legacyFamily || cleanFontFamily(st.fontFamily, item.fontName),
            fontSize: Math.max(4, Math.round(fontSize * ptToDoc)),
            fontWeight: /bold|black|heavy|semibold/i.test(item.fontName) ? 700 : 400,
            fontStyle: /italic|oblique/i.test(item.fontName) ? 'italic' : 'normal',
            color,
            textAlign: 'left', verticalAlign: 'middle', lineHeight: 1.2,
            letterSpacing: 0, whiteSpace: 'pre', backgroundColor: 'transparent',
        };
        textEls.push({
            type: 'text', name: str.trim().slice(0, 24) || 'Text',
            x: Math.round(xPt * ptToDoc), y: Math.round(topPt * ptToDoc),
            width: Math.max(8, Math.ceil(wPt * ptToDoc) + 2),
            height: Math.max(8, Math.ceil(hPt * ptToDoc)),
            rotation,
            content: str,
            // Illustrator measured this box with ITS font; ours is usually wider,
            // so the box is re-fitted (and re-centred) once the faces are loaded.
            _fitAnchor: {
                x: xPt * ptToDoc, w: wPt * ptToDoc,
                cx: cxPt * ptToDoc, cy: cyPt * ptToDoc,
                centred: true,
            },
            styles: elStyles,
        });
    });
    return { textEls, erases };
}

/**
 * Rasterise a page, but never let the import hang on it. pdf.js drives its canvas
 * renderer off requestAnimationFrame, which a browser stops firing in a hidden or
 * throttled tab — so a render started while the user tabs away can stay pending
 * forever. The raster only supplies sampled colours, so a timeout costs fidelity,
 * not the import. Returns true when the page actually painted.
 */
async function renderPageBounded(page, ctx, viewport, ms = 20000) {
    const task = page.render({ canvasContext: ctx, viewport, background: '#ffffff' });
    let timer = null;
    try {
        return await Promise.race([
            task.promise.then(() => true),
            new Promise((resolve) => { timer = setTimeout(() => resolve(false), ms); }),
        ]);
    } catch {
        return false;
    } finally {
        clearTimeout(timer);
    }
}

export async function pdfToDocument(file) {
    const data = new Uint8Array(await file.arrayBuffer());
    let pdf;
    try {
        pdf = await pdfjsLib.getDocument({ data }).promise;
    } catch (err) {
        throw new Error(
            'Could not read this file as a PDF/AI. If it is an older Illustrator '
            + 'file, re-save it with "Create PDF Compatible File" enabled, or export it as SVG.',
        );
    }
    const page = await pdf.getPage(1);
    const vpt = page.getViewport({ scale: 1 });
    const pageWpt = vpt.width;
    const pageHpt = vpt.height;
    const { docW, docH, scale: ptToDoc } = fitDocSize(pageWpt, pageHpt);

    // Illustrator layer (OCG) names.
    const ocgNames = {};
    try {
        const occ = await pdf.getOptionalContentConfig();
        const gs = occ && occ.getGroups ? occ.getGroups() : {};
        Object.entries(gs || {}).forEach(([id, g]) => { ocgNames[id] = (g && g.name) || 'Layer'; });
    } catch { /* no layers */ }

    const tc = await page.getTextContent({ disableCombineTextItems: false });
    const hasText = tc.items.some((it) => it.str && it.str.trim());
    const renderScale = Math.min(3, Math.max(1.5, 2400 / Math.max(pageWpt, pageHpt)));

    // --- Vector decomposition (layer-by-layer) ---
    let vector = null;
    try { vector = await pdfPageToVectorLayers(page, vpt, ocgNames); } catch { vector = null; }

    if (vector && countAllPaths(vector.objects) > 0) {
        const out = [];
        const groups = [];
        let z = 0;
        let nP = 0; let nG = 0;

        let vectorTextCtx = null;
        if (hasText) {
            const renderViewport = page.getViewport({ scale: renderScale });
            const textCanvas = document.createElement('canvas');
            textCanvas.width = Math.floor(renderViewport.width);
            textCanvas.height = Math.floor(renderViewport.height);
            vectorTextCtx = textCanvas.getContext('2d', { willReadFrequently: true });
            const ok = await renderPageBounded(page, vectorTextCtx, renderViewport);
            if (!ok) vectorTextCtx = null; // colours fall back; the import still lands
        }

        const bboxOfPaths = (paths) => {
            let bb = null;
            paths.forEach((p) => {
                const b = bboxOfObj(p);
                if (b) bb = unionBox(bb, b);
            });
            return bb;
        };

        /**
         * Mirror the Illustrator object tree into nested editor groups: every PDF
         * group becomes a sub-layer (with its own children), every filled path an
         * element. Wrapper groups holding a single child are dissolved later by
         * pruneGroups, so the tree that lands in the panel matches Illustrator's.
         */
        const emit = (obj, parentId, depth) => {
            if (obj.kind !== 'group') {
                const bb = bboxOfObj(obj);
                if (!bb || bb.maxx - bb.minx < 0.5 || bb.maxy - bb.miny < 0.5) return;
                out.push({
                    type: 'image', name: `Path ${nP += 1}`,
                    src: pathsToSvgUrl([obj], bb), fit: 'fill', styles: {},
                    x: Math.round(bb.minx * ptToDoc), y: Math.round(bb.miny * ptToDoc),
                    width: Math.max(1, Math.round((bb.maxx - bb.minx) * ptToDoc)),
                    height: Math.max(1, Math.round((bb.maxy - bb.miny) * ptToDoc)),
                    zIndex: z++, groupId: parentId,
                });
                return;
            }
            const paths = [];
            collectPaths(obj, paths);
            if (!paths.length) return;
            // Very deep / very large groups stay as one flattened vector piece so a
            // complex illustration can't explode into thousands of layers.
            if (depth >= 6 || paths.length > 120) {
                const bb = bboxOfPaths(paths);
                if (!bb || bb.maxx - bb.minx < 0.5 || bb.maxy - bb.miny < 0.5) return;
                out.push({
                    type: 'image', name: `Group ${nG += 1}`,
                    src: pathsToSvgUrl(paths, bb), fit: 'fill', styles: {},
                    x: Math.round(bb.minx * ptToDoc), y: Math.round(bb.miny * ptToDoc),
                    width: Math.max(1, Math.round((bb.maxx - bb.minx) * ptToDoc)),
                    height: Math.max(1, Math.round((bb.maxy - bb.miny) * ptToDoc)),
                    zIndex: z++, groupId: parentId,
                });
                return;
            }
            const gid = randId('imp_grp');
            groups.push({ id: gid, name: `Group ${nG += 1}`, parentId });
            obj.objects.forEach((child) => emit(child, gid, depth + 1));
        };

        // Everything sits under the Illustrator layer (OCG) name — the root sub-layer.
        const rootId = randId('imp_layer');
        groups.push({ id: rootId, name: vector.layerName || 'Layer 1', parentId: null });
        vector.objects.forEach((obj) => emit(obj, rootId, 0));

        if (out.length) {
            // Live text on top (rare for outlined designs) — its own sibling layer.
            if (hasText) {
                const { textEls } = buildTextElements(tc, pageHpt, ptToDoc, renderScale, vectorTextCtx, vectorTextCtx?.canvas.width || 0, vectorTextCtx?.canvas.height || 0, page);
                if (textEls.length) {
                    const tgid = randId('imp_text');
                    groups.push({ id: tgid, name: 'Text', parentId: null });
                    textEls.forEach((e) => { e.zIndex = z++; e.groupId = tgid; out.push(e); });
                }
            }
            // Illustrator measured with ITS fonts; re-fit around the substitutes so
            // long runs aren't clipped by a box sized for a narrower face.
            await refitImportedText(out);
            return {
                canvas: { width: docW, height: docH, background: '#ffffff' },
                elements: out, groups,
                vector: true, layerCount: out.length, groupCount: groups.length,
                extractedText: out.filter((e) => e.type === 'text').length,
                pageCount: pdf.numPages,
            };
        }
    }

    // --- Fallback: high-DPI raster base + editable text on top ---
    const rvp = page.getViewport({ scale: renderScale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.floor(rvp.width);
    canvas.height = Math.floor(rvp.height);
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    await renderPageBounded(page, ctx, rvp);

    const { textEls, erases } = hasText
        ? buildTextElements(tc, pageHpt, ptToDoc, renderScale, ctx, canvas.width, canvas.height, page)
        : { textEls: [], erases: [] };
    // Knock the live glyphs out of the raster so they aren't drawn twice. Rotated
    // runs need the eraser turned with them, or the patch bleeds over neighbours.
    erases.forEach((e) => {
        ctx.save();
        ctx.translate(e.cx, e.cy);
        if (e.rotation) ctx.rotate((e.rotation * Math.PI) / 180);
        ctx.fillStyle = e.bg;
        ctx.fillRect(-e.w / 2, -e.h / 2, e.w, e.h);
        ctx.restore();
    });
    const src = canvas.toDataURL('image/png');

    const out = [{
        type: 'image', name: 'Artwork', locked: true, editableByUser: false,
        x: 0, y: 0, width: docW, height: docH, src, fit: 'fill', styles: {}, zIndex: 0,
    }];
    const groups = [];
    if (textEls.length) {
        const gid = randId('imp_text');
        groups.push({ id: gid, name: 'Text' });
        let z = 1;
        textEls.forEach((e) => { e.zIndex = z++; e.groupId = gid; out.push(e); });
        await refitImportedText(out);
    }
    return {
        canvas: { width: docW, height: docH, background: '#ffffff' },
        elements: out, groups,
        extractedText: textEls.length,
        pageCount: pdf.numPages,
        raster: textEls.length === 0,
    };
}

/* ------------------------------------------------------------------ *
 *  Photoshop (PSD / PSB) → layer-by-layer import
 *
 *  Photoshop's layer tree maps straight onto the editor's nested groups:
 *  a layer group becomes a sub-layer, a raster layer becomes an image at its
 *  exact bounds, and a live type layer becomes EDITABLE text (font, size,
 *  colour and alignment read from the PSD's type data). Hidden layers stay
 *  hidden, per-layer opacity and blend mode carry over.
 * ------------------------------------------------------------------ */

const PSD_BLEND_TO_CSS = {
    normal: 'normal', multiply: 'multiply', screen: 'screen', overlay: 'overlay',
    darken: 'darken', lighten: 'lighten', 'color dodge': 'color-dodge',
    'color burn': 'color-burn', 'hard light': 'hard-light', 'soft light': 'soft-light',
    difference: 'difference', exclusion: 'exclusion', hue: 'hue',
    saturation: 'saturation', color: 'color', luminosity: 'luminosity',
};

const clamp01 = (v) => (v < 0 ? 0 : (v > 1 ? 1 : v));

/**
 * CMYK → sRGB, as a press prints it rather than as arithmetic suggests.
 *
 * The obvious model — one minus the ink, per channel — puts 100% cyan at
 * #00FFFF, a pure screen colour no ink makes, and solid K at #000000. Photoshop
 * separates through a profile instead, and the difference is not subtle: the
 * cyan an imported template is built on came in glowing, and every rich dark
 * came in too blue and too heavy.
 *
 * So interpolate between the colours the inks ACTUALLY print, measured on coated
 * stock: the eight corners of the CMY cube (paper, the three inks, their three
 * two-ink overprints, and three-ink composite black), trilinear in the ink
 * amounts, then black ink pulls the result toward the near-neutral that solid K
 * prints as. Not a colour-managed transform — no ICC engine here, and these
 * files embed no profile — but it lands within a few units of what Photoshop's
 * own thumbnail shows for this artwork, where the naive model was 20+ out.
 *
 * Indexed by ink bits: C = 4, M = 2, Y = 1.
 */
const CMYK_CORNERS = [
    [255, 255, 255], // paper
    [255, 242, 0], //   Y
    [236, 0, 140], //   M
    [237, 28, 36], //   M + Y  → red
    [0, 174, 239], //   C
    [0, 166, 81], //    C + Y  → green
    [46, 49, 146], //   C + M  → blue
    [35, 31, 32], //    C + M + Y → composite black
];
/** What solid black ink prints as — near-neutral, not #000000. */
const CMYK_BLACK = [35, 31, 32];

/** PSD CMYK descriptors are 0-100 ink percentages. */
function cmykToRgb255(c, m, y, k) {
    const C = clamp01(c / 100);
    const M = clamp01(m / 100);
    const Y = clamp01(y / 100);
    const K = clamp01(k / 100);
    let r = 0;
    let g = 0;
    let b = 0;
    for (let i = 0; i < 8; i += 1) {
        const w = ((i & 4) ? C : 1 - C) * ((i & 2) ? M : 1 - M) * ((i & 1) ? Y : 1 - Y);
        if (w <= 0) continue;
        const p = CMYK_CORNERS[i];
        r += w * p[0];
        g += w * p[1];
        b += w * p[2];
    }
    return {
        r: r + (CMYK_BLACK[0] - r) * K,
        g: g + (CMYK_BLACK[1] - g) * K,
        b: b + (CMYK_BLACK[2] - b) * K,
    };
}

/**
 * Undo ag-psd's naive CMYK→RGB so the pixels can be re-converted properly.
 *
 * ag-psd writes `R = 255(1-C)(1-K)` (and the same for G/M and B/Y) — three
 * equations, four inks, so nothing recovers the separation in general. What DOES
 * is the assumption every separation is built on: the lightest of C, M and Y
 * carries no ink of its own, everything they shared having moved into K. That
 * makes the recovery exact for any colour with an unused ink, which is nearly
 * all flat artwork (a 100% cyan bar round-trips to 100% cyan), and a close
 * approximation for rich composites.
 */
function naiveRgbToCmyk(r, g, b) {
    const mx = Math.max(r, g, b) / 255;
    if (mx <= 0) return [0, 0, 0, 100];
    return [
        (1 - (r / 255) / mx) * 100,
        (1 - (g / 255) / mx) * 100,
        (1 - (b / 255) / mx) * 100,
        (1 - mx) * 100,
    ];
}

/**
 * Re-convert one layer's pixels through the model above, in place.
 *
 * Cached per colour: flat artwork is a handful of fills over millions of pixels,
 * so the cache turns a per-pixel transform into a per-fill one. A layer varied
 * enough to blow past the cap (a CMYK photo) stops filling the cache — there is
 * nothing to reuse there anyway — rather than growing it without bound.
 */
const CMYK_CACHE_MAX = 1 << 16;
function recolorCmykPixels(data) {
    const cache = new Map();
    for (let i = 0; i < data.length; i += 4) {
        if (!data[i + 3]) continue; // fully transparent: nothing to see
        const key = (data[i] << 16) | (data[i + 1] << 8) | data[i + 2];
        let out = cache.get(key);
        if (out === undefined) {
            const [c, m, y, k] = naiveRgbToCmyk(data[i], data[i + 1], data[i + 2]);
            const rgb = cmykToRgb255(c, m, y, k);
            out = (clamp255(rgb.r) << 16) | (clamp255(rgb.g) << 8) | clamp255(rgb.b);
            if (cache.size < CMYK_CACHE_MAX) cache.set(key, out);
        }
        data[i] = out >> 16;
        data[i + 1] = (out >> 8) & 0xff;
        data[i + 2] = out & 0xff;
    }
}

/** CIE Lab → sRGB through the D50 white point Photoshop's Lab mode uses. */
function labToRgb255(l, a, bb) {
    const fy = (l + 16) / 116;
    const fx = fy + a / 500;
    const fz = fy - bb / 200;
    const f = (t) => (t > 6 / 29 ? t * t * t : 3 * (6 / 29) * (6 / 29) * (t - 4 / 29));
    const X = 0.96422 * f(fx);
    const Y = f(fy);
    const Z = 0.82521 * f(fz);
    const gamma = (u) => 255 * (u <= 0.0031308
        ? 12.92 * u
        : 1.055 * Math.pow(Math.max(0, u), 1 / 2.4) - 0.055);
    return {
        r: gamma(3.1338561 * X - 1.6168667 * Y - 0.4906146 * Z),
        g: gamma(-0.9787684 * X + 1.9161415 * Y + 0.0334540 * Z),
        b: gamma(0.0719453 * X - 0.2289914 * Y + 1.4052427 * Z),
    };
}

/** PSD HSB descriptors: h in degrees, s/b 0-100. */
function hsbToRgb255(h, s, v) {
    const S = clamp01(s / 100);
    const V = clamp01(v / 100);
    const hh = ((((h % 360) + 360) % 360) / 60);
    const c = V * S;
    const x = c * (1 - Math.abs((hh % 2) - 1));
    const m = V - c;
    const t = [[c, x, 0], [x, c, 0], [0, c, x], [0, x, c], [x, 0, c], [c, 0, x]][Math.floor(hh) % 6];
    return { r: (t[0] + m) * 255, g: (t[1] + m) * 255, b: (t[2] + m) * 255 };
}

/**
 * Any ag-psd colour descriptor → RGB 0-255. Print-ready files carry CMYK (and
 * occasionally Lab) colours for text, strokes, glows and gradient stops, so
 * without this every one of them silently fell back to the caller's default.
 *
 * The single-`k` greyscale descriptor is deliberately NOT mapped: ag-psd uses
 * `{k}` for a 0-255 grey level in one reader and a 0-100 Photoshop "K" ink
 * percentage in another, and those two run in opposite directions — guessing
 * wrong flips black text to white. Callers keep their own fallback instead.
 */
/**
 * Full-ink value of a CMYK descriptor. ag-psd hands us CMYK on TWO different
 * scales and nothing in the object says which: descriptor colours (layer effects,
 * gradient stops) keep Photoshop's 0-100 ink percentages, while a TYPE layer's
 * colours come out of the text engine, where they are 0-1 floats multiplied by
 * 255. Read on the wrong scale every ink above 39% saturates, so a 74/62/50/34
 * navy and a 60% grey both collapsed to solid black. Callers say which they hold.
 */
const CMYK_PERCENT = 100;
const CMYK_BYTE = 255;

function psdColorRgb(c, cmykMax = CMYK_PERCENT) {
    if (!c || typeof c !== 'object') return null;
    if ('r' in c && 'g' in c && 'b' in c) return { r: c.r, g: c.g, b: c.b };
    if ('fr' in c) return { r: c.fr * 255, g: c.fg * 255, b: c.fb * 255 };
    if ('c' in c && 'm' in c && 'y' in c && 'k' in c) {
        const p = 100 / (cmykMax || CMYK_PERCENT);
        return cmykToRgb255(c.c * p, c.m * p, c.y * p, c.k * p);
    }
    if ('l' in c && 'a' in c && 'b' in c) return labToRgb255(c.l, c.a, c.b);
    if ('h' in c && 's' in c && 'b' in c) return hsbToRgb255(c.h, c.s, c.b);
    return null;
}

function psdColorHex(c, fallback = '#000000', cmykMax = CMYK_PERCENT) {
    const rgb = psdColorRgb(c, cmykMax);
    return rgb ? toHex(rgb.r, rgb.g, rgb.b) : fallback;
}

/** Enabled entries of one effect slot (ag-psd stores single objects or arrays). */
function psdEnabledFx(slot) {
    const arr = Array.isArray(slot) ? slot : (slot ? [slot] : []);
    return arr.filter((x) => x && x.enabled !== false && x.present !== false);
}

/** Interpolated opacity (0-1) at a gradient location from PSD opacityStops. */
function opacityAt(stops, loc) {
    if (!stops || !stops.length) return 1;
    const s = [...stops].sort((a, b) => a.location - b.location);
    if (loc <= s[0].location) return s[0].opacity ?? 1;
    if (loc >= s[s.length - 1].location) return s[s.length - 1].opacity ?? 1;
    for (let i = 1; i < s.length; i += 1) {
        if (loc <= s[i].location) {
            const a = s[i - 1];
            const b = s[i];
            const t = (loc - a.location) / ((b.location - a.location) || 1);
            return (a.opacity ?? 1) + ((b.opacity ?? 1) - (a.opacity ?? 1)) * t;
        }
    }
    return 1;
}

/** ag-psd sizes/distances are UnitsValue objects ({units, value}) or plain numbers. */
function uv(v, def = 0) {
    if (typeof v === 'number') return v;
    if (v && typeof v.value === 'number') return v.value;
    return def;
}

function newCanvas(w, h) {
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.ceil(w));
    c.height = Math.max(1, Math.ceil(h));
    return c;
}

function psdRgba(color, alpha = 1) {
    const c = psdColorRgb(color) || { r: 0, g: 0, b: 0 };
    return `rgba(${clamp255(c.r)},${clamp255(c.g)},${clamp255(c.b)},${alpha})`;
}

/** First colour of a glow/stroke that may be defined by a gradient instead. */
function effectColor(e) {
    if (e.color) return e.color;
    const stops = e.gradient && e.gradient.colorStops;
    if (stops && stops.length) return stops[0].color;
    return { r: 0, g: 0, b: 0 };
}

/** Silhouette of `src` grown by `r` px, filled with `color`. */
function dilateSilhouette(src, r, color, W, H, px, py) {
    const out = newCanvas(W, H);
    const c = out.getContext('2d');
    const steps = Math.max(8, Math.min(48, Math.ceil(r * 6)));
    for (let i = 0; i < steps; i += 1) {
        const a = (i / steps) * Math.PI * 2;
        c.drawImage(src, px + Math.cos(a) * r, py + Math.sin(a) * r);
    }
    c.drawImage(src, px, py);
    c.globalCompositeOperation = 'source-in';
    c.fillStyle = color;
    c.fillRect(0, 0, W, H);
    return out;
}

/** Everything OUTSIDE `src`'s alpha, as opaque black — the base for inset effects. */
function inverseSilhouette(src, W, H, px, py) {
    const inv = newCanvas(W, H);
    const c = inv.getContext('2d');
    c.fillStyle = '#000';
    c.fillRect(0, 0, W, H);
    c.globalCompositeOperation = 'destination-out';
    c.drawImage(src, px, py);
    return inv;
}

/**
 * Bake Photoshop layer effects into the layer bitmap: Colour / Gradient Overlay,
 * Drop Shadow, Outer Glow, Inner Shadow, Inner Glow and Stroke. ag-psd hands back
 * the RAW pixels with every effect kept separate, so without this a shape that
 * Photoshop paints with a gradient and a drop shadow imports as a flat, shadowless
 * blob in whatever colour happens to sit underneath.
 *
 * Outside effects grow past the layer bounds, so this returns the padding used and
 * the caller shifts/enlarges the element to match.
 *
 * Not reproduced: Bevel & Emboss, Satin, Pattern Overlay (Photoshop-internal
 * shading models with no faithful 2D-canvas equivalent).
 */
function renderPsdLayerBitmap(srcCanvas, layer) {
    const fx = (layer.effects && !layer.effects.disabled) ? layer.effects : null;
    const drop = fx ? psdEnabledFx(fx.dropShadow) : [];
    const inner = fx ? psdEnabledFx(fx.innerShadow) : [];
    const oGlow = fx ? psdEnabledFx(fx.outerGlow) : [];
    const iGlow = fx ? psdEnabledFx(fx.innerGlow) : [];
    const strokes = fx ? psdEnabledFx(fx.stroke) : [];
    const overlays = fx ? [
        ...psdEnabledFx(fx.solidFill).map((e) => ({ ...e, _kind: 'solid' })),
        ...psdEnabledFx(fx.gradientOverlay).map((e) => ({ ...e, _kind: 'gradient' })),
    ] : [];

    const w = srcCanvas.width;
    const h = srcCanvas.height;

    // How far the outside effects reach beyond the layer box.
    let pad = 0;
    drop.forEach((e) => { pad = Math.max(pad, uv(e.distance) + uv(e.size) + Math.abs(uv(e.choke))); });
    oGlow.forEach((e) => { pad = Math.max(pad, uv(e.size) + Math.abs(uv(e.choke))); });
    strokes.forEach((e) => { if (e.position !== 'inside') pad = Math.max(pad, uv(e.size)); });
    pad = Math.ceil(pad) + (pad ? 2 : 0);

    if (!fx || (!drop.length && !inner.length && !oGlow.length && !iGlow.length
        && !strokes.length && !overlays.length)) {
        return { canvas: srcCanvas, pad: 0 };
    }

    const W = w + pad * 2;
    const H = h + pad * 2;
    const out = newCanvas(W, H);
    const octx = out.getContext('2d');

    // Canvas shadow settings stay active for EVERY subsequent draw — including the
    // knock-out/clip passes below, where a second shadow would erase (or keep) far
    // more than intended. Always clear them before compositing.
    const clearShadow = (c) => {
        c.shadowColor = 'transparent';
        c.shadowBlur = 0;
        c.shadowOffsetX = 0;
        c.shadowOffsetY = 0;
    };

    const paint = (src, e, comp) => {
        octx.save();
        const blend = PSD_BLEND_TO_CSS[e.blendMode];
        if (comp) octx.globalCompositeOperation = comp;
        else if (blend && blend !== 'normal') octx.globalCompositeOperation = blend;
        octx.globalAlpha = e.opacity == null ? 1 : e.opacity;
        octx.drawImage(src, 0, 0);
        octx.restore();
    };

    // ---- behind the layer: drop shadow, then outer glow ----
    drop.forEach((e) => {
        const dist = uv(e.distance);
        const a = ((e.angle ?? 120) * Math.PI) / 180;
        // PS `angle` is the LIGHT direction; the shadow falls the opposite way.
        const ox = -dist * Math.cos(a);
        const oy = dist * Math.sin(a);
        const sh = newCanvas(W, H);
        const c = sh.getContext('2d');
        c.shadowColor = psdRgba(e.color, 1);
        c.shadowBlur = uv(e.size);
        c.shadowOffsetX = ox;
        c.shadowOffsetY = oy;
        c.drawImage(srcCanvas, pad, pad);
        // Photoshop's default "layer knocks out drop shadow"
        clearShadow(c);
        c.globalCompositeOperation = 'destination-out';
        c.drawImage(srcCanvas, pad, pad);
        paint(sh, e);
    });

    oGlow.forEach((e) => {
        const sh = newCanvas(W, H);
        const c = sh.getContext('2d');
        c.shadowColor = psdRgba(effectColor(e), 1);
        c.shadowBlur = Math.max(1, uv(e.size));
        c.drawImage(srcCanvas, pad, pad);
        clearShadow(c);
        c.globalCompositeOperation = 'destination-out';
        c.drawImage(srcCanvas, pad, pad);
        paint(sh, e);
    });

    // ---- the layer itself ----
    octx.drawImage(srcCanvas, pad, pad);

    // ---- overlays (clipped to the layer's own pixels) ----
    overlays.forEach((e) => {
        const layerFx = newCanvas(W, H);
        const lctx = layerFx.getContext('2d');
        if (e._kind === 'solid') {
            lctx.fillStyle = psdColorHex(e.color, '#000000');
            lctx.fillRect(0, 0, W, H);
        } else {
            const g = e.gradient || {};
            const stops = [...(g.colorStops || [])].sort((a, b) => a.location - b.location);
            if (!stops.length) return;
            // PSD locations are 0..1 in modern files; older ones use 0..4096.
            const maxLoc = Math.max(...stops.map((s) => s.location));
            const norm = (v) => (maxLoc > 1.5 ? v / 4096 : v);
            const angle = ((e.angle ?? 0) * Math.PI) / 180;
            // PS: 0° points right, positive angles rotate counter-clockwise (screen y is down)
            const dx = Math.cos(angle);
            const dy = -Math.sin(angle);
            const sc = e.scale != null ? e.scale : 1;
            const len = (Math.abs(w * dx) + Math.abs(h * dy)) * sc;
            const cx = pad + w / 2 + (e.offset?.x || 0) * w;
            const cy = pad + h / 2 + (e.offset?.y || 0) * h;
            let grad;
            if (e.type === 'radial') {
                grad = lctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(1, len / 2));
            } else {
                grad = lctx.createLinearGradient(
                    cx - (dx * len) / 2, cy - (dy * len) / 2,
                    cx + (dx * len) / 2, cy + (dy * len) / 2,
                );
            }
            stops.forEach((s) => {
                const loc = Math.max(0, Math.min(1, norm(s.location)));
                const pos = e.reverse ? 1 - loc : loc;
                const al = opacityAt(g.opacityStops, s.location);
                grad.addColorStop(Math.max(0, Math.min(1, pos)), psdRgba(s.color, al));
            });
            lctx.fillStyle = grad;
            lctx.fillRect(0, 0, W, H);
        }
        lctx.globalCompositeOperation = 'destination-in';
        lctx.drawImage(srcCanvas, pad, pad);
        paint(layerFx, e);
    });

    // ---- inset effects: inner shadow, inner glow ----
    const insetFrom = (e, ox, oy, src) => {
        const sh = newCanvas(W, H);
        const c = sh.getContext('2d');
        c.shadowColor = psdRgba(effectColor(e), 1);
        c.shadowBlur = Math.max(1, uv(e.size));
        c.shadowOffsetX = ox;
        c.shadowOffsetY = oy;
        c.drawImage(src, 0, 0);
        // keep only what lands inside the layer
        clearShadow(c);
        c.globalCompositeOperation = 'destination-in';
        c.drawImage(srcCanvas, pad, pad);
        paint(sh, e);
    };
    if (inner.length || iGlow.length) {
        const inv = inverseSilhouette(srcCanvas, W, H, pad, pad);
        inner.forEach((e) => {
            const dist = uv(e.distance);
            const a = ((e.angle ?? 120) * Math.PI) / 180;
            insetFrom(e, -dist * Math.cos(a), dist * Math.sin(a), inv);
        });
        iGlow.forEach((e) => insetFrom(e, 0, 0, inv));
    }

    // ---- stroke (topmost) ----
    strokes.forEach((e) => {
        const size = Math.max(0.5, uv(e.size, 1));
        const color = psdRgba(effectColor(e), 1);
        let ring;
        if (e.position === 'inside') {
            const inv = inverseSilhouette(srcCanvas, W, H, pad, pad);
            ring = dilateSilhouette(inv, size, color, W, H, 0, 0);
            const c = ring.getContext('2d');
            c.globalCompositeOperation = 'destination-in';
            c.drawImage(srcCanvas, pad, pad); // keep the band inside the shape
        } else {
            const r = e.position === 'center' ? size / 2 : size;
            ring = dilateSilhouette(srcCanvas, r, color, W, H, pad, pad);
            if (e.position !== 'center') {
                const c = ring.getContext('2d');
                c.globalCompositeOperation = 'destination-out';
                c.drawImage(srcCanvas, pad, pad); // outside only
            }
        }
        paint(ring, e);
    });

    return { canvas: out, pad };
}

/**
 * Apply a Photoshop layer mask to the layer bitmap. The mask is a separate
 * greyscale channel with its OWN bounds; pixels outside those bounds take the
 * mask's `defaultColor` (0 = hidden, 255 = shown). Without this, masked artwork
 * imports unmasked and paints over everything it was meant to be hidden behind.
 */
function applyPsdMask(srcCanvas, layer) {
    const mask = layer.mask;
    if (!mask || mask.disabled || !mask.canvas) return srcCanvas;

    const w = srcCanvas.width;
    const h = srcCanvas.height;
    const lx = layer.left || 0;
    const ly = layer.top || 0;

    // Build the mask's alpha in the LAYER's coordinate space.
    const alpha = newCanvas(w, h);
    const actx = alpha.getContext('2d');
    const def = mask.defaultColor == null ? 0 : mask.defaultColor;
    actx.fillStyle = def >= 128 ? '#fff' : '#000';
    actx.fillRect(0, 0, w, h);
    actx.drawImage(mask.canvas, (mask.left || 0) - lx, (mask.top || 0) - ly);

    // Greyscale → alpha (white shows, black hides).
    const img = actx.getImageData(0, 0, w, h);
    const d = img.data;
    for (let i = 0; i < d.length; i += 4) {
        d[i + 3] = (d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114) | 0;
        d[i] = 0; d[i + 1] = 0; d[i + 2] = 0;
    }
    actx.putImageData(img, 0, 0);

    const out = newCanvas(w, h);
    const octx = out.getContext('2d');
    octx.drawImage(srcCanvas, 0, 0);
    octx.globalCompositeOperation = 'destination-in';
    octx.drawImage(alpha, 0, 0);
    return out;
}

/**
 * Clip a layer to the alpha of the layer it is clipped onto (Photoshop's
 * clipping mask / "clip to layer below"). `base` is that layer's bitmap plus
 * where it sits, both in document space.
 */
function applyPsdClipping(srcCanvas, layer, base) {
    if (!base || !base.canvas) return srcCanvas;
    const w = srcCanvas.width;
    const h = srcCanvas.height;
    const out = newCanvas(w, h);
    const octx = out.getContext('2d');
    octx.drawImage(srcCanvas, 0, 0);
    octx.globalCompositeOperation = 'destination-in';
    octx.drawImage(base.canvas, base.x - (layer.left || 0), base.y - (layer.top || 0));
    return out;
}

const PSD_JUSTIFY = { left: 'left', center: 'center', right: 'right', justifyAll: 'justify' };

/**
 * Map a PSD font to one the editor actually offers, so the panel shows a real
 * selection and the canvas + export resolve the SAME face.
 *
 * Photoshop writes PostScript names ("Montserrat-SemiBold", "OpenSans"), so the
 * style suffix is stripped and camel case split back into words before looking
 * the family up in the full catalogue. That catalogue is the whole Google Fonts
 * library, so most designs now keep their real typeface instead of collapsing
 * to Inter.
 */
function psdFontFamily(fontName) {
    const raw = String(fontName || '').replace(/-.*$/, '').replace(/([a-z])([A-Z])/g, '$1 $2').trim();
    if (getFamily(raw)) return getFamily(raw).name;
    // "OpenSans" with no camel-case boundary to split on, and similar squashes.
    const squashed = String(fontName || '').replace(/-.*$/, '').replace(/\s+/g, '').toLowerCase();
    const loose = FONT_CATALOG.find((f) => f.name.replace(/\s+/g, '').toLowerCase() === squashed);
    if (loose) return loose.name;
    if (/serif|times|georgia|garamond|playfair|merriweather/i.test(fontName)) return 'Georgia';
    if (/mono|courier|consolas/i.test(fontName)) return 'Courier New';
    return 'Inter';
}

/**
 * Widest rendered line, measured with the font the editor will ACTUALLY use.
 * The PSD box is the ink extent of Photoshop's own font; after substitution the
 * glyphs are usually wider, and the element box (overflow:hidden) clipped them.
 */
let textProbeCtx = null;
/** The glyphs that will actually be drawn — `textTransform` changes the run. */
const transformedText = (content, styles) => {
    const t = styles?.textTransform;
    if (t === 'uppercase') return String(content || '').toUpperCase();
    if (t === 'lowercase') return String(content || '').toLowerCase();
    return String(content || '');
};
function measureTextWidth(content, styles) {
    if (typeof document === 'undefined') return 0;
    if (!textProbeCtx) textProbeCtx = document.createElement('canvas').getContext('2d');
    const ctx = textProbeCtx;
    ctx.font = `${styles.fontStyle || 'normal'} ${styles.fontWeight || 400} ${styles.fontSize}px ${resolveFontStack(styles)}`;
    const spacing = Number(styles.letterSpacing) || 0;
    let max = 0;
    transformedText(content, styles).split('\n').forEach((line) => {
        // CSS letter-spacing goes after EVERY character, the last one included —
        // counting the gaps between them instead left a tracked heading one unit
        // short of the width it lays out at, which is enough to wrap its last word.
        const w = ctx.measureText(line).width + (spacing ? spacing * line.length : 0);
        if (w > max) max = w;
    });
    return max;
}

/** Load the faces a measurement depends on, so metrics aren't taken from a fallback. */
async function ensureImportFonts(specs) {
    if (typeof document === 'undefined' || !document.fonts?.load) return;
    const seen = new Map();
    specs.forEach((s) => {
        const key = `${s.fontStyle || 'normal'} ${s.fontWeight || 400} ${s.fontSize}px "${s.fontFamily}"`;
        if (seen.has(key)) return;
        seen.set(key, s);
        // The substituted family is almost never already on the page — an import
        // can name any of ~1900 Google families — so request the stylesheet
        // BEFORE waiting on it, or every box is measured against a fallback.
        const italic = s.fontStyle === 'italic';
        ensureFontFamily(s.fontFamily, [resolveVariant(s.fontFamily, Number(s.fontWeight) || 400, italic)]);
    });
    const specStrings = [...seen.keys()];
    // Which families we can actually fetch — a name the file made up, or a font
    // only the author had installed, will never arrive and must not be waited on.
    const families = [...new Set([...seen.values()].map((s) => s.fontFamily))]
        .filter((f) => f && getFamily(f));
    // `document.fonts.check` is NOT the test it looks like: it answers "can this be
    // rendered without a download", which is TRUE for a family with no @font-face
    // registered at all — precisely the state a just-requested stylesheet is in. So
    // ask the font set whether a face for the family has actually arrived.
    const familyArrived = (family) => {
        for (const face of document.fonts) {
            const name = String(face.family || '').replace(/^["']|["']$/g, '');
            if (name === family && face.status === 'loaded') return true;
        }
        return false;
    };
    const waitingFor = () => families.filter((f) => !familyArrived(f));
    // `document.fonts.load` resolves with NOTHING while the family is unregistered,
    // and `fonts.ready` only waits on loads already in flight, so both can settle
    // before the faces exist — and then every box is measured against a fallback
    // and comes out too narrow to hold its own text. Keep asking, bounded.
    for (let attempt = 0; attempt < 20; attempt += 1) {
        await Promise.allSettled(specStrings.map((f) => document.fonts.load(f)));
        try { await document.fonts.ready; } catch { /* ignore */ }
        if (!waitingFor().length) return;
        await new Promise((r) => setTimeout(r, 50));
    }
}

/**
 * Re-fit an imported text element to the text it will actually render, keeping it
 * centred on the spot Photoshop/Illustrator put it. Called after fonts are loaded.
 *
 * `anchor.centred` forces the box to stay centred on the anchor point regardless
 * of alignment — required for ROTATED text, where the element is laid out in its
 * own unrotated frame and the editor spins it about its centre, so growing the
 * box off one edge would swing the glyphs away from where they belong.
 */
function refitTextElement(el) {
    const s = el.styles || {};
    const anchor = el._fitAnchor;
    if (!anchor) return;
    // AREA type ("box" in Photoshop) has an author-drawn frame and WRAPS inside
    // it. Its stored string carries no newlines at the soft breaks, so measuring
    // it as one run — the point-text path below — read a paragraph as a single
    // line thousands of pixels wide, blew the element far off the page and threw
    // away the wrapping the layout depends on. Keep the frame; only let it grow
    // DOWNWARD (it is top-aligned) when the substituted font needs another line.
    if (anchor.box) {
        const min = minTextBox(el, s, el.width);
        if (min) {
            // A single word longer than the frame would be clipped, not wrapped.
            if (min.width > el.width) el.width = min.width;
            if (min.height > el.height) el.height = min.height;
        }
        delete el._fitAnchor;
        return;
    }
    const lines = Math.max(1, String(el.content || '').split('\n').length);
    const measured = measureTextWidth(el.content, s);
    // Slack for side bearings / italic overhang, so nothing touches the edge.
    // A `tight` anchor is one LINE of a split layer: the ink box spans the whole
    // layer, so it says where the line is aligned but not how wide it is.
    const width = Math.ceil(Math.max(anchor.tight ? 0 : anchor.w, measured) + s.fontSize * 0.35);
    // Line box plus a little slack for descenders, so nothing sits on the edge.
    const height = Math.ceil(s.fontSize * (s.lineHeight || 1.2) * lines + s.fontSize * 0.3);

    const align = s.textAlign || 'left';
    let x = anchor.x;
    if (anchor.centred || align === 'center') x = anchor.cx - width / 2;
    else if (align === 'right') x = anchor.x + anchor.w - width;

    el.x = Math.round(x);
    el.y = Math.round(anchor.cy - height / 2); // vertical centre keeps the baseline put
    el.width = width;
    el.height = height;
    delete el._fitAnchor;
}

/** Load the substituted faces, then re-fit every anchored text box in place. */
async function refitImportedText(elements) {
    const textEls = elements.filter((e) => e.type === 'text' && e._fitAnchor);
    if (!textEls.length) return;
    await ensureImportFonts(textEls.map((e) => e.styles));
    textEls.forEach(refitTextElement);
}

/**
 * Colour spans from a Photoshop type layer's style runs.
 *
 * Runs are lengths over the raw string, and the element stores the normalised one
 * (CRLF → LF, trailing return dropped), so the colours are laid out per raw
 * character and then carried through that same normalisation.
 */
function psdRunColorSpans(t, rawText, baseColor) {
    const runs = Array.isArray(t.styleRuns) ? t.styleRuns : null;
    if (!runs || runs.length < 2) return [];

    const raw = [...rawText];
    const colors = new Array(raw.length).fill(null);
    let at = 0;
    runs.forEach((run) => {
        const len = Math.max(0, Number(run?.length) || 0);
        const hex = psdColorHex(run?.style?.fillColor, null, CMYK_BYTE);
        for (let i = at; i < Math.min(raw.length, at + len); i += 1) colors[i] = hex;
        at += len;
    });
    if (!colors.some((c) => c && c !== baseColor)) return [];

    // Same rewrite the content gets, carrying each character's colour along.
    const kept = [];
    for (let i = 0; i < raw.length; i += 1) {
        const ch = raw[i];
        if (ch === '\r') {
            kept.push({ ch: '\n', color: colors[i] });
            if (raw[i + 1] === '\n') i += 1; // CRLF is one line break
        } else {
            kept.push({ ch, color: colors[i] });
        }
    }
    if (kept.length && kept[kept.length - 1].ch === '\n') kept.pop();
    return spansFromPerChar(kept.map((c) => c.color), baseColor);
}

/**
 * The style a type layer actually renders in.
 *
 * `text.style` is the layer's DEFAULT sheet and, in files written by Photoshop
 * itself, routinely carries no font, no size and no colour — those live on the
 * style RUNS. Preferring the default whenever it exists (it is an object, so
 * always truthy) meant every such layer imported at the 16px black fallback:
 * a 5x-scaled 36pt headline came in as 80px, the 50pt date columns as 16px.
 * Take the run that covers the most characters as the base and let the default
 * sheet fill the gaps — the element model holds one size per element, so the
 * dominant run is the one worth being right about.
 */
function dominantRunStyle(runs) {
    if (!Array.isArray(runs) || !runs.length) return null;
    let best = null;
    let bestLen = -1;
    runs.forEach((run) => {
        const len = Number(run?.length) || 0;
        if (run?.style && len > bestLen) { best = run.style; bestLen = len; }
    });
    return best;
}

/**
 * The document-space frame of an AREA ("box") type layer, or null if this layer
 * isn't one — or if its frame can't be trusted.
 *
 * `boxBounds` is [left, top, right, bottom] in the type layer's own space, which
 * the layer transform maps into the document. Rotated frames are left to the
 * point-text path: the element is laid out unrotated and spun about its centre,
 * so a turned frame needs the ink box, not this rectangle. Templates re-scaled
 * from a larger original also keep a transform in the OLD coordinate system, so
 * the frame is only used when it actually lands on the rasterised ink.
 *
 * @param ink the layer's ink bounds, already in document space.
 */
function areaTextFrame(t, tf, rotated, scale, ink) {
    if (t.shapeType !== 'box' || rotated || !tf) return null;
    const b = t.boxBounds;
    if (!Array.isArray(b) || b.length < 4 || !b.every((v) => Number.isFinite(v))) return null;
    const sx = Number.isFinite(tf[0]) && tf[0] ? tf[0] : 1;
    const sy = Number.isFinite(tf[3]) && tf[3] ? tf[3] : 1;
    const x = ((Number(tf[4]) || 0) + b[0] * sx) * scale;
    const y = ((Number(tf[5]) || 0) + b[1] * sy) * scale;
    const w = (b[2] - b[0]) * sx * scale;
    const h = (b[3] - b[1]) * sy * scale;
    if (!(w >= 1) || !(h >= 1)) return null;
    // The ink sits inside the frame, so the smaller of the two is the yardstick.
    const hit = Math.max(0, Math.min(x + w, ink.x + ink.w) - Math.max(x, ink.x))
        * Math.max(0, Math.min(y + h, ink.y + ink.h) - Math.max(y, ink.y));
    const smallest = Math.min(w * h, Math.max(1, ink.w * ink.h));
    if (hit < smallest * 0.6) return null;
    return { x, y, w, h };
}

/** Where the editor puts a line's baseline inside its box, as a fraction of the
 *  font size: half the leading, then the ascender. */
const TEXT_ASCENT = 0.8;
const firstBaselineIn = (lineHeight, fontSize) => ((lineHeight - 1) / 2 + TEXT_ASCENT) * fontSize;

/**
 * A type layer whose style runs are exactly its lines — one run per line, each
 * with its own size — split into one run per line, or null if it isn't one.
 *
 * A name block set as "SMAKO" over "ATSON HILSS" in a single layer is the common
 * case. The element model holds ONE size per element, so such a layer can only
 * import at one of its sizes; splitting it on the line breaks it already has
 * costs nothing and keeps both. Anything subtler (a size change mid-line) still
 * imports as one element at its dominant run's size.
 */
function psdRunLines(t, rawText) {
    const runs = Array.isArray(t.styleRuns) ? t.styleRuns : null;
    if (!runs || runs.length < 2) return null;
    // Offsets of every raw line, terminator included — that is what the runs count.
    const ranges = [];
    let start = 0;
    for (let i = 0; i < rawText.length; i += 1) {
        const ch = rawText[i];
        if (ch !== '\r' && ch !== '\n') continue;
        const end = (ch === '\r' && rawText[i + 1] === '\n') ? i + 2 : i + 1;
        ranges.push({ start, end });
        start = end;
        i = end - 1;
    }
    if (start < rawText.length) ranges.push({ start, end: rawText.length });
    if (ranges.length !== runs.length) return null;

    let at = 0;
    const lines = [];
    for (let i = 0; i < runs.length; i += 1) {
        const len = Number(runs[i]?.length) || 0;
        if (at !== ranges[i].start || at + len !== ranges[i].end) return null;
        at += len;
        lines.push({ text: rawText.slice(ranges[i].start, ranges[i].end), style: runs[i]?.style || {} });
    }
    // One size throughout is what colorSpans already keep as a single element.
    const sizes = new Set(lines.map((l) => l.style.fontSize));
    if (sizes.size < 2) return null;
    return lines;
}

/**
 * Point text split across its per-line style runs, or null when the layer isn't
 * one the split can place safely. Each line keeps its own size, tracking, caps
 * and colour, and sits on the baseline Photoshop would have advanced it to.
 */
function psdTextLineElements(layer, scale, name, ctx) {
    const {
        t, tf, rotated, rawText, boxLeft, boxTop, boxW, boxH, legacyBanglaLayer,
    } = ctx;
    if (rotated || !tf || t.shapeType === 'box' || legacyBanglaLayer) return null;
    const lines = psdRunLines(t, rawText);
    if (!lines) return null;

    const tfScale = Math.hypot(Number(tf[2]) || 0, Number(tf[3]) || 0) || 1;
    const unit = tfScale * scale;
    let baseline = (Number(tf[5]) || 0) * scale;
    // Same reality check the single-element path makes: a template re-scaled from
    // a bigger original keeps a transform in the OLD space, and its baseline then
    // has nothing to do with the ink we can see.
    if (!(baseline >= boxTop - boxH && baseline <= boxTop + boxH * 2)) return null;

    const centreX = boxLeft + boxW / 2;
    const out = [];
    lines.forEach((line, i) => {
        const style = { ...(t.style || {}), ...line.style };
        const fontSize = Math.max(4, Math.round((style.fontSize || 16) * unit));
        // Photoshop advances to each line by ITS leading; auto-leading is 1.2x.
        if (i) {
            const lead = (!style.autoLeading && style.leading)
                ? style.leading * unit
                : fontSize * 1.2;
            baseline += lead;
        }
        // One line renders no leading of its own, so the box is a plain 1.2 line box.
        const lineBoxH = fontSize * 1.2;
        const boxTopY = baseline - firstBaselineIn(1.2, fontSize);
        const fontName = (style.font && style.font.name) || '';
        const content = line.text.replace(/\r\n?/g, '\n').replace(/\n$/, '');
        out.push({
            type: 'text',
            name: (content || name || 'Text').trim().slice(0, 24) || 'Text',
            x: Math.round(boxLeft),
            y: Math.round(boxTopY),
            width: Math.max(8, Math.round(boxW)),
            height: Math.max(8, Math.round(lineBoxH)),
            rotation: 0,
            content,
            opacity: layer.opacity == null ? 1 : layer.opacity,
            visible: !layer.hidden,
            // The line's own run length is what sizes it; the layer's ink box only
            // says where its left/centre/right edge is.
            _fitAnchor: {
                x: boxLeft, w: boxW, cx: centreX, cy: boxTopY + lineBoxH / 2, tight: true,
            },
            styles: {
                fontFamily: psdFontFamily(fontName),
                fontSize,
                fontWeight: /bold|black|heavy|semibold/i.test(fontName) ? 700 : 400,
                fontStyle: /italic|oblique/i.test(fontName) ? 'italic' : 'normal',
                color: psdColorHex(style.fillColor, '#111111', CMYK_BYTE),
                textAlign: PSD_JUSTIFY[(t.paragraphStyle || {}).justification] || 'left',
                ...(style.fontCaps ? { textTransform: 'uppercase' } : null),
                verticalAlign: 'middle',
                lineHeight: 1.2,
                letterSpacing: style.tracking
                    ? Math.round((style.tracking / 1000) * fontSize * 10) / 10
                    : 0,
                whiteSpace: 'pre-wrap',
                backgroundColor: 'transparent',
                padding: 0,
                paddingTop: 0,
                paddingBottom: 0,
                paddingLeft: 0,
                paddingRight: 0,
                mixBlendMode: PSD_BLEND_TO_CSS[layer.blendMode] || 'normal',
            },
        });
    });
    return out;
}

/** Every element one type layer becomes — usually one, but a layer whose runs
 *  are its lines becomes one per line so each keeps its own size. */
function psdTextElements(layer, scale, name) {
    const t = layer.text;
    const tf = Array.isArray(t.transform) ? t.transform : null;
    const num = (v) => (Number.isFinite(v) ? v : 0);
    const rotation = tf ? normalizeAngle((Math.atan2(num(tf[1]), num(tf[0])) * 180) / Math.PI) : 0;
    const merged = { ...(t.style || {}), ...(dominantRunStyle(t.styleRuns) || {}) };
    const split = psdTextLineElements(layer, scale, name, {
        t,
        tf,
        rotated: Math.abs(rotation) > 0.5,
        rawText: String(t.text || ''),
        boxLeft: (layer.left || 0) * scale,
        boxTop: (layer.top || 0) * scale,
        boxW: ((layer.right || 0) - (layer.left || 0)) * scale,
        boxH: ((layer.bottom || 0) - (layer.top || 0)) * scale,
        // Bijoy text is transliterated as a whole; splitting it would cut the
        // string at offsets the conversion no longer agrees with.
        legacyBanglaLayer: isLegacyBanglaFont((merged.font && merged.font.name) || ''),
    });
    return split || [psdTextElement(layer, scale, name)];
}

function psdTextElement(layer, scale, name) {
    const t = layer.text;
    const style = { ...(t.style || {}), ...(dominantRunStyle(t.styleRuns) || {}) };
    const para = {
        ...(t.paragraphStyle || {}),
        ...(dominantRunStyle(t.paragraphStyleRuns) || {}),
    };
    // Type layers carry their own transform; its scale multiplies the font size
    // and its rotation is the ONLY record that the type is turned — the layer
    // bounds are just the rotated ink extent, so a 90° label reads as a tall,
    // narrow box and used to import as horizontal text crammed into it.
    // Read the matrix cells RAW: a quarter-turn puts an exact 0 in tf[0] and tf[3],
    // and an `|| 1` default swallows it — a 90° layer then reads as a 42° one at
    // 1.35x the type size, which is neither upright nor right.
    const tf = Array.isArray(t.transform) ? t.transform : null;
    const num = (v) => (Number.isFinite(v) ? v : 0);
    const tfScale = tf ? (Math.hypot(num(tf[2]), num(tf[3])) || 1) : 1;
    const fontSize = Math.max(4, Math.round((style.fontSize || 16) * tfScale * scale));
    const rotation = tf ? normalizeAngle((Math.atan2(num(tf[1]), num(tf[0])) * 180) / Math.PI) : 0;
    const rotated = Math.abs(rotation) > 0.5;
    // Photoshop's own line endings and trailing return are normalised away; the
    // style runs are indexed against the RAW string, so the colours are mapped
    // before that happens (psdRunColorSpans) rather than after.
    const rawText = String(t.text || '');
    const content = rawText.replace(/\r\n?/g, '\n').replace(/\n$/, '');
    const lines = Math.max(1, content.split('\n').length);
    // Leading is the gap BETWEEN lines, so Photoshop draws none of it on a
    // single-line point-text layer. Authors therefore leave whatever stale value
    // is in the type object (this card carries 4x on its slogans) and the file
    // still looks right in Photoshop. Taken at face value it inflated the element
    // box to several times the glyphs, and that empty margin overlapped the
    // neighbouring text far enough to steal its clicks. Honour leading only where
    // Photoshop itself would render it: multi-line text, or a box that can wrap —
    // and never when the layer is set to auto-leading, which is ~1.2 anyway.
    const rendersLeading = lines > 1 || t.shapeType === 'box';
    const lineHeight = (rendersLeading && style.leading && !style.autoLeading)
        ? Math.max(0.5, style.leading / (style.fontSize || 16))
        : 1.2;

    const boxLeft = (layer.left || 0) * scale;
    const boxTop = (layer.top || 0) * scale;
    const boxW = ((layer.right || 0) - (layer.left || 0)) * scale;
    const boxH = ((layer.bottom || 0) - (layer.top || 0)) * scale;

    // Photoshop point-text sits on a BASELINE (transform[5]); the layer box is
    // only the ink extent, which is shorter than a full line box. Anchor on the
    // text's visual CENTRE and let refitTextElement size the box around it — the
    // element is vertically centred, so the box can grow without shifting the text.
    const lineBoxH = fontSize * lineHeight * lines;
    // The editor puts the FIRST baseline ((lineHeight−1)/2 + ascent)·size below the
    // box top, so walk back from Photoshop's baseline to the box, then to its centre.
    const firstBaselineOffset = firstBaselineIn(lineHeight, fontSize);
    // …but only when that baseline is actually in DOCUMENT space. Templates that
    // were re-scaled from a larger original keep a type transform in the old
    // coordinate system (translations of tens of thousands of pixels), which
    // throws every text layer far off the canvas. The layer's own bounds are the
    // rasterised ink extent and are always document space, so use them as the
    // reality check — and as the anchor when the transform disagrees.
    const baseline = tf ? tf[5] * scale : null;
    const baselineInBox = baseline != null && (boxH < 1
        || (baseline >= boxTop - fontSize && baseline <= boxTop + boxH + fontSize));
    // Rotated type: the baseline walk above only holds for upright text (tf[5] is
    // the origin of a turned baseline, not a document-space y), so anchor on the
    // centre of the ink box — which is the same point either way once rotated.
    const centreY = (tf && !rotated && t.shapeType !== 'box' && baselineInBox)
        ? baseline - firstBaselineOffset + lineBoxH / 2
        : boxTop + boxH / 2;
    const centreX = boxLeft + boxW / 2;

    // The element lives in its OWN unrotated frame and the editor spins it about
    // its centre, so undo the rotation on the ink box to get the text's real run
    // length. Exact at right angles — which is what rotated type in print is.
    const absC = Math.abs(Math.cos((rotation * Math.PI) / 180));
    const absS = Math.abs(Math.sin((rotation * Math.PI) / 180));
    const runW = rotated ? (boxW * absC + boxH * absS) : boxW;

    // AREA type: Photoshop keeps the author-drawn frame in `boxBounds`, in the
    // type layer's own space, and wraps inside it. The layer bounds are only the
    // rasterised ink, and the stored string has NO break at a soft wrap — so
    // sizing an area layer from its ink (and then re-fitting it to one long run)
    // both unwrapped the paragraph and pushed the element metres off the page.
    // Take the frame, and let the text wrap in it exactly as Photoshop did.
    const frame = areaTextFrame(t, tf, rotated, scale, {
        x: boxLeft, y: boxTop, w: boxW, h: boxH,
    });
    // Photoshop puts an area frame's first baseline one ASCENDER below the frame
    // top; the editor puts it half a leading lower than that. Lift the box by the
    // difference so the first line lands where the artwork has it.
    const frameY = frame ? frame.y - ((lineHeight - 1) / 2) * fontSize : 0;

    const fontName = (style.font && style.font.name) || '';
    // Bijoy-era Photoshop type (SutonnyMJ and friends) stores Bangla as 8-bit Latin
    // bytes that only LOOK Bangla in that font — transliterate it to Unicode and
    // re-type it in a Unicode Bangla face, exactly as the PDF path does.
    const legacyBangla = isLegacyBanglaFont(fontName);
    const text = legacyBangla ? legacyBanglaToUnicode(content) : content;
    // Allow text to wrap while preserving explicit line breaks from PSD
    const whiteSpace = 'pre-wrap';

    // Photoshop keeps a word coloured differently inside the same type layer as a
    // style run; the editor keeps it as colour spans, so the layer stays one
    // element. Skipped for Bijoy text, whose transliteration moves every offset.
    const baseColor = psdColorHex(style.fillColor, '#111111', CMYK_BYTE);
    const colorSpans = legacyBangla ? [] : psdRunColorSpans(t, rawText, baseColor);

    return {
        type: 'text',
        name: (text || name || 'Text').trim().slice(0, 24) || 'Text',
        ...(colorSpans.length ? { colorSpans } : null),
        // Provisional box — refitTextElement replaces it once fonts are loaded.
        x: Math.round(frame ? frame.x : (rotated ? centreX - runW / 2 : boxLeft)),
        y: Math.round(frame ? frameY : centreY - lineBoxH / 2),
        // Initial width from PSD; refitTextElement will adjust based on actual text
        width: Math.max(8, Math.round(frame ? frame.w : runW)),
        height: Math.max(8, Math.round(frame ? frame.h : lineBoxH)),
        // An area frame's width is the column the artwork was laid out in — editing
        // the text must re-wrap inside it, never stretch it into one long line.
        ...(frame ? { textAutoSize: 'height' } : null),
        rotation,
        content: text,
        opacity: layer.opacity == null ? 1 : layer.opacity,
        visible: !layer.hidden,
        // Where Photoshop put the text: the ink box plus its centre, used to
        // re-centre the element after it is resized to fit the substituted font.
        // An area frame instead keeps its own box and only grows downward.
        _fitAnchor: frame ? { box: true } : {
            x: rotated ? centreX - runW / 2 : boxLeft,
            w: runW,
            cx: centreX,
            cy: centreY,
            // A rotated box must grow symmetrically or the glyphs swing off-place.
            centred: rotated,
        },
        styles: {
            fontFamily: legacyBangla ? legacyBanglaFamily(fontName) : psdFontFamily(fontName),
            fontSize,
            fontWeight: /bold|black|heavy|semibold/i.test(fontName) ? 700 : 400,
            fontStyle: /italic|oblique/i.test(fontName) ? 'italic' : 'normal',
            color: psdColorHex(style.fillColor, '#111111', CMYK_BYTE),
            textAlign: PSD_JUSTIFY[para.justification] || 'left',
            // All Caps / Small Caps live in the type style, not the string —
            // ignoring them imported every uppercase heading in the artwork as
            // the lowercase text the author actually typed.
            ...(style.fontCaps ? { textTransform: 'uppercase' } : null),
            // Point text is centred vertically so growing the box for a wider or
            // taller substituted font keeps the text where Photoshop drew it. An
            // area frame is anchored at its top, the way Photoshop fills it.
            verticalAlign: frame ? 'top' : 'middle',
            lineHeight,
            letterSpacing: style.tracking ? Math.round((style.tracking / 1000) * fontSize * 10) / 10 : 0,
            whiteSpace,
            backgroundColor: 'transparent',
            padding: 0,
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 0,
            paddingRight: 0,
            mixBlendMode: PSD_BLEND_TO_CSS[layer.blendMode] || 'normal',
        },
    };
}

const PSD_MODE = {
    BITMAP: 0, GRAYSCALE: 1, INDEXED: 2, RGB: 3, CMYK: 4, MULTICHANNEL: 7, DUOTONE: 8, LAB: 9,
};
// Modes we can turn into RGB. ag-psd already decodes CMYK pixels (it just keeps
// the mode off its allow-list); Duotone and Lab arrive raw in the red channel and
// are converted by normalizePsdPixels below.
const PSD_EXTRA_MODES = [PSD_MODE.CMYK, PSD_MODE.DUOTONE, PSD_MODE.LAB];

/**
 * ag-psd rejects anything outside RGB/greyscale/indexed *before* it parses a
 * single layer — so every print-ready CMYK file died with "Color mode not
 * supported". The check reads a mutable list on its reader module, so importing
 * that module directly (rather than the package entry, which doesn't re-export
 * it) lets us switch on the modes we can actually convert. Reading through the
 * same module keeps it one instance, so the flag we set is the one it tests.
 */
async function loadPsdReader() {
    try {
        const m = await import('ag-psd/dist/psdReader.js');
        if (m?.supportedColorModes && m.createReader && m.readPsd) {
            PSD_EXTRA_MODES.forEach((mode) => {
                if (!m.supportedColorModes.includes(mode)) m.supportedColorModes.push(mode);
            });
            return (buffer, options) => m.readPsd(m.createReader(buffer), options);
        }
    } catch { /* fall through to the package entry (RGB/greyscale only) */ }
    const { readPsd } = await import('ag-psd');
    return readPsd;
}

/**
 * Duotone and Lab layers come back with their raw channels dumped into R (ag-psd
 * only special-cases greyscale and CMYK), so a Duotone layer imports red-tinted
 * and a Lab layer imports as nonsense. Rewrite those pixels in place as RGB.
 *
 * CMYK layers DO arrive as RGB — through ag-psd's naive one-minus-the-ink model,
 * which is the same thing that made every imported ink glow. They are re-read
 * through the press model here, so the artwork matches the text sitting on it.
 */
const PSD_RECOLOR_MODES = [PSD_MODE.DUOTONE, PSD_MODE.LAB, PSD_MODE.CMYK];
function normalizePsdPixels(canvas, colorMode) {
    if (!PSD_RECOLOR_MODES.includes(colorMode)) return canvas;
    const ctx = canvas.getContext('2d');
    const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const d = img.data;
    if (colorMode === PSD_MODE.CMYK) {
        recolorCmykPixels(d);
        ctx.putImageData(img, 0, 0);
        return canvas;
    }
    for (let i = 0; i < d.length; i += 4) {
        if (colorMode === PSD_MODE.DUOTONE) {
            // One ink channel, stored like greyscale — mirror it into G and B.
            d[i + 1] = d[i];
            d[i + 2] = d[i];
        } else {
            // 8-bit Lab: L is 0-255 for 0-100, a and b are offset by 128.
            const rgb = labToRgb255((d[i] * 100) / 255, d[i + 1] - 128, d[i + 2] - 128);
            d[i] = clamp255(rgb.r);
            d[i + 1] = clamp255(rgb.g);
            d[i + 2] = clamp255(rgb.b);
        }
    }
    ctx.putImageData(img, 0, 0);
    return canvas;
}

const rectOf = (el) => ({ x1: el.x, y1: el.y, x2: el.x + el.width, y2: el.y + el.height });
const rectArea = (r) => Math.max(0, r.x2 - r.x1) * Math.max(0, r.y2 - r.y1);
const overlapArea = (a, b) => Math.max(0, Math.min(a.x2, b.x2) - Math.max(a.x1, b.x1))
    * Math.max(0, Math.min(a.y2, b.y2) - Math.max(a.y1, b.y1));
const unionRect = (a, b) => (a ? {
    x1: Math.min(a.x1, b.x1), y1: Math.min(a.y1, b.y1),
    x2: Math.max(a.x2, b.x2), y2: Math.max(a.y2, b.y2),
} : b);

/**
 * Two-sided print templates keep every face in ONE Photoshop document: "Front"
 * and "Back" are top-level groups occupying the SAME page rectangle. Imported
 * faithfully they land on top of each other and the design reads as broken, so
 * lay the faces out as separate boards and grow the page to hold them.
 *
 * Only fires on an unambiguous stack — every visible top-level group covers the
 * page, they all overlap each other, nothing visible sits outside a group, and
 * each group carries its OWN full-page backdrop. That last test is what keeps an
 * ordinary "background" + "content" split (where only one group owns the
 * backdrop) from being torn into two boards.
 *
 * Mutates element positions; returns the new page size, or null if it declined.
 */
function separatePsdFaces(elements, groups, docW, docH) {
    const tops = groups.filter((g) => !g.parentId);
    if (tops.length < 2) return null;

    const childGroups = new Map();
    groups.forEach((g) => {
        const key = g.parentId || null;
        if (!childGroups.has(key)) childGroups.set(key, []);
        childGroups.get(key).push(g.id);
    });
    const subtreeIds = (rootId) => {
        const seen = new Set([rootId]);
        const stack = [rootId];
        while (stack.length) {
            (childGroups.get(stack.pop()) || []).forEach((id) => {
                if (!seen.has(id)) { seen.add(id); stack.push(id); }
            });
        }
        return seen;
    };

    const page = { x1: 0, y1: 0, x2: docW, y2: docH };
    const pageArea = docW * docH;
    const boards = [];

    for (const g of tops) {
        const ids = subtreeIds(g.id);
        const own = elements.filter((e) => ids.has(e.groupId));
        const shown = own.filter((e) => e.visible !== false);
        // Hidden helper groups (bleed marks, die cut) aren't faces — leave them be.
        if (!shown.length) continue;
        const hasBackdrop = shown.some((e) => overlapArea(rectOf(e), page) >= pageArea * 0.9);
        const bounds = shown.reduce((acc, e) => unionRect(acc, rectOf(e)), null);
        if (!hasBackdrop || overlapArea(bounds, page) < pageArea * 0.8) return null;
        boards.push({ els: own, bounds, z: Math.max(...shown.map((e) => e.zIndex ?? 0)) });
    }
    if (boards.length < 2) return null;

    // Anything visible living outside these groups would be orphaned by the move.
    const claimed = new Set();
    boards.forEach((b) => b.els.forEach((e) => claimed.add(e)));
    if (elements.some((e) => e.visible !== false && !claimed.has(e))) return null;

    // They must actually be stacked — faces sharing the page, not neighbours.
    for (let i = 1; i < boards.length; i += 1) {
        const a = boards[0].bounds;
        const b = boards[i].bounds;
        if (overlapArea(a, b) < 0.8 * Math.min(rectArea(a), rectArea(b))) return null;
    }

    // Topmost group first, so the board order matches the layers panel (Front, Back).
    boards.sort((a, b) => b.z - a.z);
    // Stack along the page's short axis, so two landscape cards end up portrait
    // rather than a strip twice as wide as it is tall.
    const vertical = docW >= docH;
    const gap = Math.max(24, Math.round(Math.min(docW, docH) * 0.06));
    const span = (vertical ? docH : docW) * boards.length + gap * (boards.length - 1);
    // Laying them out would need a page the editor can't hold — leave the faces
    // stacked rather than push boards off the edge of a clamped canvas.
    if (span > CANVAS_MAX) return null;

    boards.forEach((board, i) => {
        if (!i) return;
        const dx = vertical ? 0 : i * (docW + gap);
        const dy = vertical ? i * (docH + gap) : 0;
        board.els.forEach((e) => { e.x += dx; e.y += dy; });
    });

    return {
        width: vertical ? docW : span,
        height: vertical ? span : docH,
        boardCount: boards.length,
    };
}

export async function psdToDocument(file) {
    const readPsd = await loadPsdReader();
    let psd;
    try {
        psd = readPsd(await file.arrayBuffer(), {
            skipCompositeImageData: true,
            skipThumbnail: true,
            skipLinkedFilesData: true,
        });
    } catch (err) {
        const msg = String(err?.message || err);
        if (/Color mode not supported/i.test(msg)) {
            throw new Error(
                `${msg.replace(/^.*Color mode not supported: /i, 'This Photoshop file uses an unsupported colour mode: ')}. `
                + 'Convert it in Photoshop with Image → Mode → RGB Color and re-save.',
            );
        }
        throw new Error(
            `Could not read this Photoshop file (${msg}). `
            + 'Re-save it from Photoshop with "Maximize Compatibility" enabled.',
        );
    }
    const colorMode = psd.colorMode;

    const { docW, docH, scale } = fitDocSize(psd.width, psd.height);

    const elements = [];
    const groups = [];
    let z = 0;
    let textCount = 0;

    /**
     * Walk the PSD tree. ag-psd lists children BOTTOM-first, which is the same
     * back → front order the editor's zIndex uses, so no reversing is needed.
     */
    const walk = (layers, parentId, depth, parentHidden) => {
        // Photoshop clips a `clipping` layer onto the nearest NON-clipping layer
        // below it. ag-psd lists siblings bottom-first, so that base is simply the
        // last plain layer we passed.
        let clipBase = null;
        (layers || []).forEach((layer) => {
            const name = layer.name || 'Layer';
            // A hidden GROUP hides everything inside it, even though Photoshop
            // leaves the children's own `hidden` flags untouched.
            const hidden = parentHidden || !!layer.hidden;
            if (layer.children && layer.children.length) {
                if (depth >= 8) return; // absurd nesting → skip rather than explode
                clipBase = null;
                const gid = randId('psd_grp');
                groups.push({ id: gid, name, parentId });
                walk(layer.children, gid, depth + 1, hidden);
                return;
            }
            if (layer.text && String(layer.text.text || '').trim()) {
                psdTextElements(layer, scale, name).forEach((el) => {
                    el.zIndex = z++;
                    el.groupId = parentId;
                    el.visible = !hidden;
                    elements.push(el);
                    textCount += 1;
                });
                return;
            }
            const w = (layer.right || 0) - (layer.left || 0);
            const h = (layer.bottom || 0) - (layer.top || 0);
            if (!layer.canvas || w < 1 || h < 1) return; // adjustment / empty layer

            // Mask → clipping mask → layer effects, in Photoshop's own order.
            let bmp = applyPsdMask(normalizePsdPixels(layer.canvas, colorMode), layer);
            if (layer.clipping && clipBase) bmp = applyPsdClipping(bmp, layer, clipBase);
            else clipBase = { canvas: bmp, x: layer.left || 0, y: layer.top || 0 };
            const { canvas: painted, pad } = renderPsdLayerBitmap(bmp, layer);

            elements.push({
                type: 'image', name,
                src: painted.toDataURL('image/png'),
                fit: 'fill',
                // Outside effects (drop shadow, glow, outer stroke) grow the bitmap,
                // so the element box grows with it instead of squashing the artwork.
                x: Math.round(((layer.left || 0) - pad) * scale),
                y: Math.round(((layer.top || 0) - pad) * scale),
                width: Math.max(1, Math.round((w + pad * 2) * scale)),
                height: Math.max(1, Math.round((h + pad * 2) * scale)),
                opacity: layer.opacity == null ? 1 : layer.opacity,
                visible: !hidden,
                styles: { mixBlendMode: PSD_BLEND_TO_CSS[layer.blendMode] || 'normal' },
                zIndex: z++, groupId: parentId,
            });
        });
    };
    walk(psd.children, null, 0, false);

    if (!elements.length) {
        throw new Error('No importable layers found in this Photoshop file.');
    }

    // Photoshop measured the boxes with ITS fonts; we render with substitutes that
    // are usually wider. Load the faces we'll actually use, then re-fit every text
    // box around its own text so nothing is clipped, keeping it centred in place.
    await refitImportedText(elements);

    // Runs last: it moves finished elements, so every box must already be final.
    const layout = separatePsdFaces(elements, groups, docW, docH);

    return {
        canvas: {
            width: layout?.width ?? docW,
            height: layout?.height ?? docH,
            background: '#ffffff',
        },
        elements,
        groups,
        vector: true, // same "kept your layer structure" messaging as the AI import
        layerCount: elements.length,
        groupCount: groups.length,
        extractedText: textCount,
        boardCount: layout?.boardCount ?? 1,
        pageCount: 1,
    };
}

/* ------------------------------------------------------------------ *
 *  TIFF → flat image (TIFF has no layer model; multi-page uses page 1)
 * ------------------------------------------------------------------ */

export async function tiffToDocument(file) {
    const UTIF = (await import('utif')).default || (await import('utif'));
    const buf = await file.arrayBuffer();
    let ifds;
    try {
        ifds = UTIF.decode(buf);
        if (!ifds || !ifds.length) throw new Error('no pages');
        UTIF.decodeImage(buf, ifds[0]);
    } catch (err) {
        throw new Error(`Could not read this TIFF (${err?.message || err}).`);
    }
    const page = ifds[0];
    const rgba = UTIF.toRGBA8(page);
    const w = page.width;
    const h = page.height;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    const img = ctx.createImageData(w, h);
    img.data.set(rgba);
    ctx.putImageData(img, 0, 0);

    const { docW, docH } = fitDocSize(w, h);
    return {
        canvas: { width: docW, height: docH, background: '#ffffff' },
        elements: [{
            type: 'image', name: 'Artwork', locked: true, editableByUser: false,
            x: 0, y: 0, width: docW, height: docH,
            src: canvas.toDataURL('image/png'), fit: 'fill', styles: {}, zIndex: 0,
        }],
        groups: [],
        extractedText: 0,
        pageCount: ifds.length,
        raster: true,
    };
}

/* ------------------------------------------------------------------ *
 *  Dispatch by file type
 * ------------------------------------------------------------------ */

export async function importArtworkFile(file) {
    const name = file?.name || '';
    if (/\.svg$/i.test(name)) {
        const text = await file.text();
        return svgToDocument(text);
    }
    if (/\.(ai|pdf)$/i.test(name)) {
        return pdfToDocument(file);
    }
    if (/\.(psd|psb)$/i.test(name)) {
        return psdToDocument(file);
    }
    if (/\.tiff?$/i.test(name)) {
        return tiffToDocument(file);
    }
    throw new Error('Unsupported file. Import an .ai, .pdf, .svg, .psd, .psb or .tif file.');
}
